import React, { useState, useEffect, useContext, useRef } from 'react';
import { useUserEvent } from '../hooks/useUserEvent';
import { ThemeContext } from '../App';
import { useToast } from '../components/Toast';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import ApexCharts from 'react-apexcharts';
import { supabase } from '../supabaseClient';
import Modal from 'react-modal';
import { usePlanLimits } from '../hooks/usePlanLimits';
import UpgradeModal from '../components/UpgradeModal';
import MapSelectModal from '../components/MapSelectModal';
import CameraStreamPlayer from '../components/CameraStreamPlayer';
import {
  aggregateMeteorologicalReadingsByDay,
  filterSensorReadingsByPeriod,
  resolveSensorHistoryRange,
} from './sensorReportData';
import { findCompatibleProperty } from '../irrigacao/sensorGeoValidation';
import { getSensorMetricState, getSensorReadingState } from './sensorTelemetryState';
import { loadVisibleAuthorizedProperties } from '../services/authorizedPropertiesService';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { FaArrowRight, FaBroadcastTower, FaChartLine, FaCheckCircle, FaClock, FaCloudRain, FaCloudSun, FaDatabase, FaExclamationTriangle, FaFileExcel, FaFilePdf, FaMapMarkerAlt, FaMicrochip, FaPlus, FaSeedling, FaThermometerHalf, FaTint, FaTrashAlt, FaVideo, FaWifi, FaWind } from 'react-icons/fa';
import '../styles/module-shell.css';
import '../styles/tech-components.css';
import './Sensores.css';
pdfMake.vfs = pdfFonts.vfs;

// Importação condicional do Capacitor Filesystem
let Filesystem, Directory, FileOpener;
if (typeof window !== 'undefined' && window.Capacitor) {
  try {
    // eslint-disable-next-line
    ({ Filesystem, Directory } = require('@capacitor/filesystem'));
  } catch (e) {
    // Ignora se não estiver disponível
  }
  try {
    // eslint-disable-next-line
    ({ FileOpener } = require('@capacitor-community/file-opener'));
  } catch (e) {
    // Ignora se não estiver disponível
  }
}

// Calcula dias corridos sem chuva efetiva (>5mm/dia) até hoje.
// Usa a diferença de calendário até o último dia com chuva > 5mm, em vez de
// contar linha a linha — assim uma falha de leitura pontual (dia sem dados)
// não interrompe a contagem antes da hora.
function calcularDiasEstiagem(totaisDiarios) {
  // totaisDiarios: array de { data: 'YYYY-MM-DD' ou timestamp ISO, total: number } ordenado do mais recente para o mais antigo
  if (!totaisDiarios.length) return 0;
  // data_local pode vir como timestamp completo (ex. view com date_trunc) — usa só os 10 primeiros chars (YYYY-MM-DD)
  const paraData = str => new Date(String(str).slice(0, 10) + 'T12:00:00');

  const hoje = paraData(totaisDiarios[0].data);
  const idxChuva = totaisDiarios.findIndex(d => d.total > 5);
  if (idxChuva === -1) {
    // Nenhuma chuva efetiva em todo o período consultado — usa o registro mais antigo disponível
    const maisAntiga = paraData(totaisDiarios[totaisDiarios.length - 1].data);
    return Math.round((hoje - maisAntiga) / 86400000) + 1;
  }
  const dataChuva = paraData(totaisDiarios[idxChuva].data);
  return Math.round((hoje - dataChuva) / 86400000);
}

function infoCor(dias) {
  if (dias === 0)  return { cor: '#43a047', texto: 'Sem estiagem', icone: '🟢' };
  if (dias < 5)   return { cor: '#1976d2', texto: `${dias} dia${dias > 1 ? 's' : ''} sem chuva efetiva`, icone: '🔵' };
  if (dias < 10)  return { cor: '#1976d2', texto: `${dias} dias sem chuva efetiva`, icone: '🔵' };
  if (dias < 20)  return { cor: '#f9a825', texto: `⚠️ ${dias} dias — atenção`, icone: '🟡' };
  if (dias < 30)  return { cor: '#e65100', texto: `🔴 ${dias} dias — crítico`, icone: '🟠' };
  return         { cor: '#b71c1c', texto: `🚨 ${dias} dias — estiagem severa`, icone: '🔴' };
}

// Configuração de tipos de sensor suportados na aba Sensores
const TIPOS_SENSOR = [
  { value: 'meteorologico', label: 'Meteorológico',     icone: '🌦️', cor: '#1976d2', tabelaDados: 'dados_sensores_meteorologicos' },
  { value: 'umidade_solo',  label: 'Umidade do Solo',   icone: '🌱', cor: '#43a047', tabelaDados: 'dados_sensores_solo' },
  { value: 'chuva_presenca', label: 'Chuva/Irrigação',  icone: '🚿', cor: '#0288d1', tabelaDados: 'dados_sensores_chuva' },
  { value: 'camera', label: 'Câmera', icone: '📷', cor: '#d97706', tabelaDados: null },
];

function getTipoSensorInfo(tipo) {
  return TIPOS_SENSOR.find(t => t.value === tipo) || TIPOS_SENSOR[0];
}
function getTabelaDados(tipo) {
  return getTipoSensorInfo(tipo).tabelaDados;
}
function useIsMobile(bp = 768) {
  const [v, setV] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const h = () => setV(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return v;
}

function sensorReadingDate(reading) {
  const rawDate = reading?.data_local || reading?.timestamp || reading?.created_at;
  if (!rawDate) return null;
  const parsed = new Date(rawDate);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatTelemetryAge(date) {
  if (!date) return 'Horário indisponível';
  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  if (minutes < 2) return 'Atualizada agora';
  if (minutes < 60) return `Atualizada há ${minutes} min`;
  if (minutes < 1440) return `Atualizada há ${Math.round(minutes / 60)} h`;
  return `Última leitura em ${date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`;
}

function formatCadence(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return 'Não calculada';
  if (minutes < 60) return `A cada ~${Math.max(1, Math.round(minutes))} min`;
  return `A cada ~${(minutes / 60).toFixed(minutes >= 120 ? 0 : 1)} h`;
}

function getSensorConnectionStatus(date) {
  if (!date) {
    return {
      tone: 'offline',
      label: 'Sem leitura recente',
      detail: 'Aguardando comunicação',
      icon: <FaExclamationTriangle />,
    };
  }

  const minutes = Math.max(0, (Date.now() - date.getTime()) / 60000);
  if (minutes <= 60) {
    return {
      tone: 'online',
      label: 'Atualizado',
      detail: formatTelemetryAge(date),
      icon: <FaWifi />,
    };
  }
  if (minutes <= 6 * 60) {
    return {
      tone: 'attention',
      label: 'Verificar comunicação',
      detail: formatTelemetryAge(date),
      icon: <FaClock />,
    };
  }

  return {
    tone: 'offline',
    label: 'Sem comunicação',
    detail: formatTelemetryAge(date),
    icon: <FaExclamationTriangle />,
  };
}

function isReadingFromToday(reading, referenceDate = new Date()) {
  const date = sensorReadingDate(reading);
  return Boolean(date) &&
    date.getFullYear() === referenceDate.getFullYear() &&
    date.getMonth() === referenceDate.getMonth() &&
    date.getDate() === referenceDate.getDate();
}

function formatCoordinate(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(5) : String(value);
}

function parseSensorNumber(value) {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function getSensorNumericValues(readings, key) {
  return readings
    .map(reading => parseSensorNumber(reading?.[key]))
    .filter(value => value !== null);
}

function averageSensorValues(readings, key) {
  const values = getSensorNumericValues(readings, key);
  if (!values.length) return null;
  return values.reduce((total, value) => total + value, 0) / values.length;
}

function sumSensorValues(readings, key) {
  const values = getSensorNumericValues(readings, key);
  if (!values.length) return null;
  return values.reduce((total, value) => total + value, 0);
}

function summarizeSensorTelemetry(sensor) {
  const readings = (sensor?.dados || [])
    .map(reading => ({ reading, date: sensorReadingDate(reading) }))
    .filter(item => item.date)
    .sort((a, b) => a.date - b.date);
  const latest = readings[readings.length - 1]?.reading || sensor?.dados?.[0] || null;
  const latestDate = readings[readings.length - 1]?.date || sensorReadingDate(latest);
  const intervals = readings.slice(1)
    .map((item, index) => (item.date - readings[index].date) / 60000)
    .filter(value => value > 0 && value <= 24 * 60)
    .sort((a, b) => a - b);
  const cadenceMinutes = intervals.length ? intervals[Math.floor(intervals.length / 2)] : null;
  const observedMinutes = readings.length > 1
    ? (readings[readings.length - 1].date - readings[0].date) / 60000
    : 0;
  const expectedReadings = cadenceMinutes && observedMinutes > 0
    ? Math.floor(observedMinutes / cadenceMinutes) + 1
    : readings.length;
  const coverage = expectedReadings > 0
    ? Math.min(100, Math.round((readings.length / expectedReadings) * 100))
    : null;

  return {
    latest,
    latestDate,
    readingCount: readings.length,
    cadenceMinutes,
    coverage,
  };
}

function SensorTelemetryOverview({ sensor, diasEstiagem, loadingEstiagem }) {
  if (!sensor?.dados?.length) return null;

  const type = sensor.tipo_sensor || 'meteorologico';
  const telemetry = summarizeSensorTelemetry(sensor);
  const latest = telemetry.latest || {};
  const connection = getSensorConnectionStatus(telemetry.latestDate);
  const liveDataAvailable = connection.tone !== 'offline';
  const metric = (key, icon, label, value, unit, tone) => value != null && Number.isFinite(Number(value)) ? {
    key,
    icon,
    label,
    value: liveDataAvailable ? Number(value).toFixed(1) : 'Indisponível',
    unit: liveDataAvailable ? unit : '',
    tone: liveDataAvailable ? tone : 'neutral',
  } : null;
  let metrics = [];

  if (type === 'meteorologico') {
    metrics = [
      metric('temperature', <FaThermometerHalf />, 'Temperatura atual', latest.temp_c, '°C', 'blue'),
      metric('humidity', <FaTint />, 'Umidade relativa', latest.rh_perc, '%', 'cyan'),
      metric('rain', <FaCloudRain />, 'Chuva na leitura', latest.rain_mm, 'mm', 'green'),
      metric('wind', <FaWind />, 'Vento atual', latest.wind_ms ?? latest.wind_m_s ?? latest.velocidade_vento, 'm/s', 'teal'),
    ].filter(Boolean);
    if (loadingEstiagem) {
      metrics.push({ key: 'drought', icon: <FaCloudRain />, label: 'Estiagem agrícola', value: '…', unit: '', tone: 'amber' });
    } else if (diasEstiagem != null) {
      metrics.push({
        key: 'drought',
        icon: diasEstiagem === 0 ? <FaSeedling /> : <FaCloudRain />,
        label: 'Estiagem agrícola',
        value: diasEstiagem === 0 ? 'Sem' : String(diasEstiagem),
        unit: diasEstiagem === 0 ? 'estiagem' : `dia${diasEstiagem === 1 ? '' : 's'}`,
        tone: diasEstiagem >= 20 ? 'red' : diasEstiagem >= 10 ? 'amber' : 'green',
      });
    }
  } else if (type === 'umidade_solo') {
    metrics = [
      metric('soil-moisture', <FaSeedling />, 'Umidade do solo', latest.umidade_solo_perc, '%', 'green'),
      metric('soil-temperature', <FaThermometerHalf />, 'Temperatura do solo', latest.temp_solo, '°C', 'blue'),
      metric('depth', <FaDatabase />, 'Profundidade', sensor.profundidade_cm, 'cm', 'teal'),
    ].filter(Boolean);
  } else {
    metrics = [{
      key: 'wet-state',
      icon: Number(latest.molhado) === 1 ? <FaCloudRain /> : <FaCloudSun />,
      label: 'Estado atual',
      value: Number(latest.molhado) === 1 ? 'Molhado' : Number(latest.molhado) === 0 ? 'Seco' : 'Sem leitura',
      unit: '',
      tone: Number(latest.molhado) === 1 ? 'blue' : 'amber',
    }];
  }

  return (
    <section className="sensor-telemetry-overview" aria-label="Situação atual da estação">
      <div className="sensor-telemetry-heading">
        <div>
          <span className="sensor-modal-kicker">Telemetria em campo</span>
          <h3>Situação atual</h3>
        </div>
        <span className="sensor-freshness"><FaWifi /> {formatTelemetryAge(telemetry.latestDate)}</span>
      </div>
      <div className="sensor-current-metrics">
        {metrics.map(item => (
          <article key={item.key} className={`sensor-current-metric is-${item.tone}`}>
            <span>{item.icon}</span>
            <div><strong>{item.value}<small>{item.unit}</small></strong><p>{item.label}</p></div>
          </article>
        ))}
      </div>
      <div className="sensor-telemetry-facts">
        <div><FaClock /><span>Última comunicação<strong>{telemetry.latestDate ? telemetry.latestDate.toLocaleString('pt-BR') : 'Não informada'}</strong></span></div>
        <div><FaBroadcastTower /><span>Cadência observada<strong>{formatCadence(telemetry.cadenceMinutes)}</strong></span></div>
        <div><FaDatabase /><span>Leituras carregadas<strong>{telemetry.readingCount.toLocaleString('pt-BR')}</strong></span></div>
        <div><FaCheckCircle /><span>Cobertura estimada<strong>{telemetry.coverage == null ? 'Não calculada' : `${telemetry.coverage}%`}</strong></span></div>
      </div>
    </section>
  );
}

function Sensores({ onBack, onNavigate, user, previewMode = false, previewData = null, initialContext = null }) {
  // Registra o evento de acesso à tela Sensores
  useUserEvent({ userId: user?.id, tela: 'Sensores', evento: 'page_view', comDuração: true });
  const isMobile = useIsMobile();
  
  const toast = useToast();
  const { darkMode } = useContext(ThemeContext);
  
  // Sistema de Planos
  const { verificarLimite, podeUsar, plano, loading: planLoading } = usePlanLimits(user?.id);
  // Evita mostrar cadeado de bloqueio antes do plano terminar de carregar
  const podeUsarPdf = planLoading || podeUsar('relatorios_pdf');
  const podeUsarSensoresPeriodo = planLoading || podeUsar('sensores_periodo');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeRecurso, setUpgradeRecurso] = useState('sensores');

  // Helper: data local no formato YYYY-MM-DD (evita bug de UTC com toISOString)
  function localDateStr(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // Helper: datetime local com offset de fuso para queries Supabase (timestamps em UTC)
  function toISOLocal(date) {
    const off = date.getTimezoneOffset();
    const sign = off <= 0 ? '+' : '-';
    const absOff = Math.abs(off);
    const hh = String(Math.floor(absOff / 60)).padStart(2, '0');
    const mm = String(absOff % 60).padStart(2, '0');
    const pad = n => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T` +
           `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${sign}${hh}:${mm}`;
  }

  // Estado para seleção de período
  const [periodoSelecionado, setPeriodoSelecionado] = React.useState('24h');
  const [carregandoDadosPeriodo, setCarregandoDadosPeriodo] = useState(false);
  
  // Inicializa datas para últimas 24h
  const hoje = new Date();
  const ontem = new Date(hoje.getTime() - 24*60*60*1000);
  const [dataIni, setDataIni] = useState(localDateStr(ontem));
  const [dataFim, setDataFim] = useState(localDateStr(hoje));
  // Estados de exportação
  const [exportando, setExportando] = useState(false);
  const [gerandoPDF, setGerandoPDF] = useState(false);
  const [reportFeedback, setReportFeedback] = useState(null);

  async function exportarPDF() {
    if (!sensorSelecionado || !sensorSelecionado.device_id) return;
    if (!dataIni || !dataFim) return toast.warning('Selecione o período!');

    if (!podeUsar('relatorios_pdf')) {
      setUpgradeRecurso('relatorios_pdf');
      setShowUpgradeModal(true);
      return;
    }

    setGerandoPDF(true);
    setReportFeedback({ type: 'loading', message: 'Preparando o relatório com os dados disponíveis…' });
    try {
      // Busca dados agregados diários
      const { data: dailyData, error: dailyError } = await supabase
        .from('dados_sensores_diarios')
        .select('*')
        .eq('device_id', sensorSelecionado.device_id)
        .gte('data_local', dataIni)
        .lte('data_local', dataFim)
        .order('data_local', { ascending: true });

      let data = dailyError ? [] : (dailyData || []);

      // A view diária pode ainda não ter materializado as leituras mais novas.
      // Nesse caso o relatório usa a telemetria bruta, que é a mesma fonte dos gráficos.
      if (!data.length) {
        let rawReadings = filterSensorReadingsByPeriod(sensorSelecionado.dados || [], dataIni, dataFim);
        if (!rawReadings.length) {
          rawReadings = await buscarDadosPeriodo(
            sensorSelecionado.device_id,
            dataIni,
            dataFim,
            sensorSelecionado.tipo_sensor || 'meteorologico'
          );
        }
        data = aggregateMeteorologicalReadingsByDay(
          filterSensorReadingsByPeriod(rawReadings || [], dataIni, dataFim)
        );
      }

      if (!data.length) {
        const message = dailyError
          ? `Não foi possível consultar os dados do relatório: ${dailyError.message}`
          : 'Nenhuma leitura foi encontrada no período selecionado.';
        setReportFeedback({ type: 'error', message });
        toast.warning(message);
        setGerandoPDF(false);
        return;
      }

      // Carrega logo
      async function getLogoBase64() {
        const response = await fetch('/ClimoraX_vertical.png');
        const blob = await response.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }
      const logoBase64 = await getLogoBase64();

      // Calcula estatísticas do período
      const tempMedias = data.map(d => Number(d.temp_media)).filter(v => !isNaN(v));
      const tempMaxs = data.map(d => Number(d.temp_max)).filter(v => !isNaN(v));
      const tempMins = data.map(d => Number(d.temp_min)).filter(v => !isNaN(v));
      const chuvas = data.map(d => Number(d.chuva_total)).filter(v => !isNaN(v));
      const umiMedias = data.map(d => Number(d.rh_perc_media)).filter(v => !isNaN(v));
      const totalLeituras = data.reduce((acc, d) => acc + (Number(d.leituras) || 0), 0);

      const statTempMedia = tempMedias.length ? (tempMedias.reduce((a,b)=>a+b,0)/tempMedias.length).toFixed(1) : '-';
      const statTempMax = tempMaxs.length ? Math.max(...tempMaxs).toFixed(1) : '-';
      const statTempMin = tempMins.length ? Math.min(...tempMins).toFixed(1) : '-';
      const statChuvaTotal = chuvas.length ? chuvas.reduce((a,b)=>a+b,0).toFixed(1) : '-';
      const diaMaxChuva = chuvas.length ? data.reduce((max, d) => Number(d.chuva_total) > Number(max.chuva_total) ? d : max, data[0]) : null;
      const statUmiMedia = umiMedias.length ? (umiMedias.reduce((a,b)=>a+b,0)/umiMedias.length).toFixed(1) : '-';

      // Formata data pt-BR
      const fmtData = (str) => {
        if (!str) return '-';
        // Suporta YYYY-MM-DD e YYYY-MM-DDTHH:mm:ss+00:00
        const dataPart = str.substring(0, 10);
        const parts = dataPart.split('-');
        if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        return str;
      };

      // ─── Helpers de gráficos pdfmake (canvas) ───────────────────
      const PAD_L      = 28;   // largura da coluna yAxisCol (fora do canvas)
      const INNER_L    = 2;    // offset esquerdo dentro do canvas
      const PAD_T      = 4;    // padding topo dentro do canvas
      const PAD_B      = 4;    // padding base dentro do canvas
      const CHART_H    = 100;  // altura do canvas
      const CHART_W    = 475;  // referência para xAxisLabels
      const innerPlotW = CHART_W - PAD_L - INNER_L - 4;
      const innerPlotH = CHART_H - PAD_T - PAD_B;
      // Aliases para xAxisLabels
      const plotW = CHART_W - PAD_L;
      const plotH = innerPlotH;

      // Gera ticks "bonitos" arredondados para uso no eixo Y e grid
      function niceAxis(rawMin, rawMax) {
        const range = (rawMax - rawMin) || 2;
        const roughStep = range / 3;
        const mag = Math.pow(10, Math.floor(Math.log10(roughStep)));
        const step = [1, 2, 2.5, 5, 10].map(c => c * mag).find(s => s >= roughStep) || mag * 10;
        const lo = Math.floor(rawMin / step) * step;
        const hi = Math.ceil(rawMax / step) * step;
        const ticks = [];
        for (let v = lo; v <= hi + step * 0.001; v += step)
          ticks.push(Math.round(v * 10000) / 10000);
        return ticks; // ascending
      }

      function buildLineChart(series, ticks) {
        if (!ticks || !ticks.length || !series[0].values.length) return [];
        const minV = ticks[0], maxV = ticks[ticks.length - 1];
        const range = maxV - minV || 1;
        const n = series[0].values.length;
        const x0 = INNER_L, x1 = INNER_L + innerPlotW;
        const y0 = PAD_T, y1 = PAD_T + innerPlotH;
        const shapes = [];
        // Fundo branco com borda suave
        shapes.push({ type: 'rect', x: x0, y: y0, w: innerPlotW, h: innerPlotH, color: '#ffffff', lineColor: '#cfd8e3', lineWidth: 0.5 });
        // Helper: calcula pontos mapeados de uma série
        const computePts = (vals) => {
          const r = [];
          for (let i = 0; i < n; i++) {
            if (vals[i] == null) continue;
            const px = x0 + (n > 1 ? i * innerPlotW / (n - 1) : innerPlotW / 2);
            const py = y0 + innerPlotH - ((vals[i] - minV) / range) * innerPlotH;
            r.push({ x: px, y: py });
          }
          return r;
        };
        // Preenchimento: banda max-min (3 séries) ou área sob a linha (1 série)
        if (series.length >= 3) {
          const maxPts = computePts(series[0].values);
          const minPts = computePts(series[2].values);
          if (maxPts.length >= 2 && minPts.length >= 2) {
            const poly = [...maxPts, ...[...minPts].reverse()];
            shapes.push({ type: 'polyline', points: poly, closePath: true, color: '#fce8e6', lineColor: '#fce8e6', lineWidth: 0 });
          }
        } else if (series.length === 1) {
          const lPts = computePts(series[0].values);
          if (lPts.length >= 2) {
            const fillColor = (series[0].color === '#43a047' || series[0].color === '#2e7d32') ? '#e8f5ea' : '#e3edf8';
            const poly = [...lPts, { x: lPts[lPts.length - 1].x, y: y1 }, { x: lPts[0].x, y: y1 }];
            shapes.push({ type: 'polyline', points: poly, closePath: true, color: fillColor, lineColor: fillColor, lineWidth: 0 });
          }
        }
        // Grid lines (sobre os fills)
        for (const tick of ticks) {
          const gy = y0 + innerPlotH - ((tick - minV) / range) * innerPlotH;
          const isBase = tick === minV;
          shapes.push({ type: 'line', x1: x0, y1: gy, x2: x1, y2: gy, lineWidth: isBase ? 0.8 : 0.3, lineColor: isBase ? '#b0bacc' : '#dde8f0' });
        }
        // Borda esquerda
        shapes.push({ type: 'line', x1: x0, y1: y0, x2: x0, y2: y1, lineWidth: 0.5, lineColor: '#b0bac8' });
        // Polylines das séries com pontos nas extremidades
        for (const s of series) {
          const sp = computePts(s.values);
          if (sp.length >= 2) {
            shapes.push({ type: 'polyline', lineWidth: 1.5, lineColor: s.color, closePath: false, points: sp });
            shapes.push({ type: 'ellipse', x: sp[0].x, y: sp[0].y, r1: 2, r2: 2, color: '#ffffff', lineColor: s.color, lineWidth: 1 });
            shapes.push({ type: 'ellipse', x: sp[sp.length - 1].x, y: sp[sp.length - 1].y, r1: 2, r2: 2, color: '#ffffff', lineColor: s.color, lineWidth: 1 });
          } else if (sp.length === 1) {
            shapes.push({ type: 'ellipse', x: sp[0].x, y: sp[0].y, r1: 2.5, r2: 2.5, lineWidth: 0, color: s.color });
          }
        }
        return [{ canvas: shapes, margin: [0, 0, 0, 0] }];
      }

      function buildBarChart(values, color, ticks) {
        const vals = values.filter(v => v != null && v > 0);
        if (!vals.length) return [];
        const maxV = (ticks && ticks.length) ? ticks[ticks.length - 1] : Math.max(...vals);
        const n = values.length;
        const barW = Math.max(2, innerPlotW / n - 1.5);
        const x0 = INNER_L, x1 = INNER_L + innerPlotW;
        const y0 = PAD_T, y1 = PAD_T + innerPlotH;
        const shapes = [];
        // Fundo branco com borda suave
        shapes.push({ type: 'rect', x: x0, y: y0, w: innerPlotW, h: innerPlotH, color: '#ffffff', lineColor: '#cfd8e3', lineWidth: 0.5 });
        if (ticks) {
          for (const tick of ticks) {
            if (tick < 0) continue;
            const gy = y0 + innerPlotH - (tick / maxV) * innerPlotH;
            shapes.push({ type: 'line', x1: x0, y1: gy, x2: x1, y2: gy, lineWidth: tick === 0 ? 0.8 : 0.3, lineColor: tick === 0 ? '#b0bacc' : '#dde8f0' });
          }
        }
        shapes.push({ type: 'line', x1: x0, y1: y0, x2: x0, y2: y1, lineWidth: 0.5, lineColor: '#b0bac8' });
        for (let i = 0; i < n; i++) {
          if (values[i] == null || values[i] === 0) continue;
          const bh = Math.min((values[i] / maxV) * innerPlotH, innerPlotH);
          const bx = x0 + i * innerPlotW / n + (innerPlotW / n - barW) / 2;
          shapes.push({ type: 'rect', x: bx, y: y1 - bh, w: barW, h: bh, color, lineWidth: 0 });
        }
        return [{ canvas: shapes, margin: [0, 0, 0, 0] }];
      }

      // Coluna do eixo Y: labels alinhados com os ticks do canvas
      // ticks = array ascending [min, ..., max] (mesmos usados no buildLineChart/buildBarChart)
      function yAxisCol(ticks, decimais) {
        const d = decimais !== undefined ? decimais : 0;
        const reversed = [...ticks].reverse(); // [max, ..., min]
        const n = reversed.length;
        // Pixels entre ticks consecutivos (uniforme)
        const tickGapPx = innerPlotH / (n - 1);
        // 1 spacer row (alinha topo do canvas → primeiro tick) + N linhas de labels
        // Spacer height = PAD_T (texto do label fica +/- centrado no tick)
        const spacerH = Math.max(1, PAD_T - 3);
        const heights = [spacerH, ...Array(n - 1).fill(tickGapPx)];
        const body = [
          [{ text: '', border: [false,false,false,false] }],
          ...reversed.map(v => [{
            text: Number(v).toFixed(d),
            fontSize: 6.5,
            color: '#777',
            alignment: 'right',
            border: [false,false,false,false],
            margin: [0, 0, 3, 0]
          }])
        ];
        return {
          width: PAD_L,
          table: { widths: [PAD_L - 3], heights, body },
          layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 }
        };
      }

      function xAxisLabels(labels) {
        if (!labels.length) return { text: '', margin: [0, 0, 0, 8] };
        const nVis = Math.min(labels.length, 7);
        const step = labels.length <= nVis ? 1 : Math.floor((labels.length - 1) / (nVis - 1));
        const items = [];
        for (let i = 0; i < labels.length; i += step) {
          const lbl = labels[i].length > 5 ? labels[i].substring(0, 5) : labels[i];
          items.push({ text: lbl, xFrac: i / Math.max(1, labels.length - 1) });
        }
        const LABEL_W = 26;
        const cols = [];
        let prevX = 0;
        for (const item of items) {
          const x = item.xFrac * plotW;
          const labelStart = Math.max(prevX, x - LABEL_W / 2);
          const gap = labelStart - prevX;
          if (gap > 0) cols.push({ text: '', width: gap });
          cols.push({ text: item.text, fontSize: 6, color: '#999', alignment: 'center', width: LABEL_W, noWrap: true });
          prevX = labelStart + LABEL_W;
        }
        const remaining = plotW - prevX;
        if (remaining > 0) cols.push({ text: '', width: remaining });
        return { columns: cols, margin: [PAD_L, 0, 0, 8], columnGap: 0 };
      }

      // Título de seção com barra de acento colorida à esquerda
      const chartSectionTitle = (label, accentColor) => ({
        columns: [
          { canvas: [{ type: 'rect', x: 0, y: 0, w: 3, h: 10, color: accentColor, lineWidth: 0 }], width: 7 },
          { text: label, color: '#444', fontSize: 8, bold: true, margin: [0, 1, 0, 0] }
        ],
        margin: [0, 4, 0, 3]
      });

      // Item de legenda com quadrado colorido
      const legendSquare = (color, label) => [
        { canvas: [{ type: 'rect', x: 0, y: 2, w: 10, h: 5, color, lineWidth: 0 }], width: 12 },
        { text: label, fontSize: 6, color: '#555', width: 'auto', margin: [0, 0, 8, 0] }
      ];

      // Decide agrupamento: ≤2 dias → por hora, >2 dias → por dia
      const ini = new Date(dataIni + 'T00:00:00');
      const fim = new Date(dataFim + 'T23:59:59');
      const diffDias = Math.ceil((fim - ini) / (1000 * 60 * 60 * 24));
      const usarAgrupamentoPorHora = diffDias <= 2;

      const tsIni = toISOLocal(ini);
      const tsFim = toISOLocal(fim);

      let chartLabels = [];
      let tempMediaSerie = [];
      let tempMaxSerie = [];
      let tempMinSerie = [];
      let chuvaSerie = [];
      let umiSerie = [];

      if (usarAgrupamentoPorHora) {
        // Busca dados brutos para agrupamento por hora
        const { data: rawData } = await supabase
          .from('dados_sensores_meteorologicos')
          .select('timestamp, temp_c, rain_mm, rh_perc')
          .eq('device_id', sensorSelecionado.device_id)
          .gte('timestamp', tsIni)
          .lte('timestamp', tsFim)
          .order('timestamp', { ascending: true });

        const totalHoras = Math.min(48, diffDias * 24);
        let ultimoDia = null;
        for (let i = 0; i < totalHoras; i++) {
          const hora = new Date(ini.getTime() + i * 60 * 60 * 1000);
          const diaAtual = hora.getDate();
          // Quando cobre 2 dias, prefixar a primeira hora de cada dia com DD/MM
          const label = (diffDias > 1 && ultimoDia !== diaAtual)
            ? `${hora.getDate().toString().padStart(2,'0')}/${(hora.getMonth()+1).toString().padStart(2,'0')}`
            : `${hora.getHours().toString().padStart(2,'0')}h`;
          ultimoDia = diaAtual;
          chartLabels.push(label);

          const dadosHora = (rawData || []).filter(d => {
            const dt = new Date(d.timestamp);
            return dt.getFullYear() === hora.getFullYear() &&
              dt.getMonth() === hora.getMonth() &&
              dt.getDate() === hora.getDate() &&
              dt.getHours() === hora.getHours();
          });

          const avgTemp = averageSensorValues(dadosHora, 'temp_c');
          const rainTotal = sumSensorValues(dadosHora, 'rain_mm');
          const avgHumidity = averageSensorValues(dadosHora, 'rh_perc');
          tempMediaSerie.push(avgTemp === null ? null : Number(avgTemp.toFixed(2)));
          tempMaxSerie.push(null);
          tempMinSerie.push(null);
          chuvaSerie.push(rainTotal === null ? null : Number(rainTotal.toFixed(2)));
          umiSerie.push(avgHumidity === null ? null : Number(avgHumidity.toFixed(2)));
        }
      } else {
        // Agrupamento por dia usando a view diária já carregada
        // Labels curtos DD/MM para caber bem nos eixos
        chartLabels = data.map(d => {
          const s = (d.data_local || '').substring(0, 10);
          const parts = s.split('-');
          return parts.length === 3 ? `${parts[2]}/${parts[1]}` : s;
        });
        tempMediaSerie = data.map(d => parseSensorNumber(d.temp_media));
        tempMaxSerie = data.map(d => parseSensorNumber(d.temp_max));
        tempMinSerie = data.map(d => parseSensorNumber(d.temp_min));
        chuvaSerie = data.map(d => parseSensorNumber(d.chuva_total));
        umiSerie = data.map(d => parseSensorNumber(d.rh_perc_media));
      }

      // Insights automáticos (sempre usa dados diários para maior precisão)
      const insights = [];
      const tempValsValidos = usarAgrupamentoPorHora
        ? tempMediaSerie.filter(v => v != null)
        : tempMediaSerie.filter(v => v != null);
      if (tempValsValidos.length >= 3) {
        const primeira = tempValsValidos.slice(0, Math.ceil(tempValsValidos.length / 3));
        const ultima = tempValsValidos.slice(-Math.ceil(tempValsValidos.length / 3));
        const avgPrimeira = primeira.reduce((a,b)=>a+b,0)/primeira.length;
        const avgUltima = ultima.reduce((a,b)=>a+b,0)/ultima.length;
        const diff = avgUltima - avgPrimeira;
        if (diff > 1.5) insights.push({ text: `Tendencia de aquecimento: temperatura subiu ~${diff.toFixed(1)}°C no periodo.` });
        else if (diff < -1.5) insights.push({ text: `Tendencia de resfriamento: temperatura caiu ~${Math.abs(diff).toFixed(1)}°C no periodo.` });
        else insights.push({ text: 'Temperatura estavel ao longo do periodo, sem tendencia significativa.' });
      }
      if (!usarAgrupamentoPorHora) {
        const amplitudes = data.filter(d => d.temp_max != null && d.temp_min != null).map(d => Number(d.temp_max) - Number(d.temp_min));
        if (amplitudes.length) {
          const ampMedia = (amplitudes.reduce((a,b)=>a+b,0)/amplitudes.length).toFixed(1);
          insights.push({ text: `Amplitude termica media de ${ampMedia}°C/dia (diferenca entre maxima e minima).` });
        }
        const diasComChuva = data.filter(d => Number(d.chuva_total) > 0).length;
        const diasSemChuva = data.length - diasComChuva;
        if (data.length > 1) insights.push({ text: `${diasComChuva} dia(s) com chuva e ${diasSemChuva} dia(s) seco(s) no periodo.` });
        if (diaMaxChuva && Number(diaMaxChuva.chuva_total) > 0)
          insights.push({ text: `Maior acumulado diario: ${Number(diaMaxChuva.chuva_total).toFixed(1)} mm em ${fmtData(diaMaxChuva.data_local)}.` });
        const umiMins = data.map(d => d.rh_perc_min != null ? Number(d.rh_perc_min) : null).filter(v => v != null);
        if (umiMins.length) {
          const umiMinAbs = Math.min(...umiMins);
          const diaUmiMin = data.find(d => Number(d.rh_perc_min) === umiMinAbs);
          insights.push({ text: `Umidade minima registrada: ${umiMinAbs.toFixed(0)}% em ${fmtData(diaUmiMin?.data_local)}.` });
        }
      } else {
        const chuvaTotal = chuvaSerie.filter(v => v != null && v > 0).reduce((a,b)=>a+b,0);
        if (chuvaTotal > 0) insights.push({ text: `Chuva acumulada no periodo: ${chuvaTotal.toFixed(1)} mm.` });
        const horasComChuva = chuvaSerie.filter(v => v != null && v > 0).length;
        if (horasComChuva > 0) insights.push({ text: `Precipitacao registrada em ${horasComChuva} hora(s) do periodo.` });
      }
      // Cobertura de dados
      if (totalLeituras > 0) insights.push({ text: `Total de ${totalLeituras} leituras registradas em ${data.length} dia${data.length > 1 ? 's' : ''} de periodo.` });

      // Séries de temp para y-axis labels
      const allTemps = [...tempMediaSerie, ...tempMaxSerie, ...tempMinSerie].filter(v => v != null);
      const tempYMin = allTemps.length ? Math.min(...allTemps) : 0;
      const tempYMax = allTemps.length ? Math.max(...allTemps) : 40;
      const umiValsOk = umiSerie.filter(v => v != null);
      const umiYMin = umiValsOk.length ? Math.min(...umiValsOk) : 0;
      const umiYMax = umiValsOk.length ? Math.max(...umiValsOk) : 100;
      const chuvaValsOk = chuvaSerie.filter(v => v != null);
      // Em modo horário, rain_mm pode estar zerado mesmo com chuva registrada nos dados diários
      // Usa statChuvaTotal como referência de escala quando a série horária estiver zerada
      const chuvaSerieEfetiva = chuvaValsOk.some(v => v > 0)
        ? chuvaSerie
        : data.map(d => d.chuva_total != null ? Number(d.chuva_total) : null);
      const chuvaEfetivaOk = chuvaSerieEfetiva.filter(v => v != null && v > 0);
      const chuvaYMax = chuvaEfetivaOk.length ? Math.max(...chuvaEfetivaOk) : (Number(statChuvaTotal) || 0);
      const temMin_max_disponivel = !usarAgrupamentoPorHora && tempMaxSerie.some(v => v != null);

      // Ticks "nice" para cada gráfico (coordena eixo Y e grid do canvas)
      const tempTicks  = niceAxis(tempYMin, tempYMax);
      const umiTicks   = niceAxis(umiYMin, umiYMax);
      const chuvaTicks = niceAxis(0, chuvaYMax);


      const docDefinition = {
        pageSize: 'A4',
        pageMargins: [28, 14, 28, 22],
        defaultStyle: { font: 'Roboto' },
        content: [
          // Header logo + título
          {
            columns: [
              { image: logoBase64, width: 68, height: 68, margin: [0, 0, 8, 0] },
              {
                stack: [
                  { text: 'Relatório de Estação Meteorológica', color: '#003B73', fontSize: 11, bold: true, margin: [0, 0, 0, 2] },
                  { text: new Date().toLocaleDateString('pt-BR') + '  ' + new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }), color: '#777', fontSize: 7 }
                ],
                margin: [0, 20, 0, 0]
              },
              { text: '', width: '*' }
            ],
            margin: [0, 0, 0, 6]
          },
          // Linha separadora
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 535, y2: 0, lineWidth: 1, lineColor: '#e0e0e0' }], margin: [0, 0, 0, 8] },
          // Informações da estação
          {
            table: {
              widths: ['30%', '30%', '40%'],
              body: [
                [
                  { text: 'ESTAÇÃO', fontSize: 7, color: '#999', bold: true, border: [false,false,false,true], borderColor: ['#fff','#fff','#fff','#e0e0e0'], margin: [0,0,0,1] },
                  { text: 'DEVICE ID', fontSize: 7, color: '#999', bold: true, border: [false,false,false,true], borderColor: ['#fff','#fff','#fff','#e0e0e0'], margin: [0,0,0,1] },
                  { text: 'PERÍODO', fontSize: 7, color: '#999', bold: true, border: [false,false,false,true], borderColor: ['#fff','#fff','#fff','#e0e0e0'], margin: [0,0,0,1] },
                ],
                [
                  { text: sensorSelecionado.nome_sensor || sensorSelecionado.device_id, fontSize: 9, color: '#222', bold: true, border: [false,false,false,false], margin: [0,3,0,0] },
                  { text: sensorSelecionado.device_id, fontSize: 9, color: '#222', border: [false,false,false,false], margin: [0,3,0,0] },
                  { text: `${fmtData(dataIni)} – ${fmtData(dataFim)}`, fontSize: 9, color: '#222', border: [false,false,false,false], margin: [0,3,0,0] },
                ]
              ]
            },
            layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingLeft: () => 0, paddingRight: () => 12, paddingTop: () => 0, paddingBottom: () => 4, fillColor: () => '#fafafa' },
            margin: [0, 0, 0, 10]
          },
          // Resumo executivo
          { text: 'RESUMO DO PERÍODO', color: '#666', fontSize: 9, bold: true, margin: [0, 0, 0, 6] },
          {
            table: {
              widths: ['*', '*', '*', '*', '*', '*'],
              body: [
                [
                  { text: 'TEMP. MÉDIA', fontSize: 7, color: '#999', bold: true, border: [false,false,false,true], borderColor: ['#fff','#fff','#fff','#e0e0e0'], fillColor: '#fafafa', margin: [0,0,0,1] },
                  { text: 'TEMP. MÁX.', fontSize: 7, color: '#999', bold: true, border: [false,false,false,true], borderColor: ['#fff','#fff','#fff','#e0e0e0'], fillColor: '#fafafa', margin: [0,0,0,1] },
                  { text: 'TEMP. MÍN.', fontSize: 7, color: '#999', bold: true, border: [false,false,false,true], borderColor: ['#fff','#fff','#fff','#e0e0e0'], fillColor: '#fafafa', margin: [0,0,0,1] },
                  { text: 'CHUVA TOTAL', fontSize: 7, color: '#999', bold: true, border: [false,false,false,true], borderColor: ['#fff','#fff','#fff','#e0e0e0'], fillColor: '#fafafa', margin: [0,0,0,1] },
                  { text: 'UMID. MÉDIA', fontSize: 7, color: '#999', bold: true, border: [false,false,false,true], borderColor: ['#fff','#fff','#fff','#e0e0e0'], fillColor: '#fafafa', margin: [0,0,0,1] },
                  { text: 'LEITURAS', fontSize: 7, color: '#999', bold: true, border: [false,false,false,true], borderColor: ['#fff','#fff','#fff','#e0e0e0'], fillColor: '#fafafa', margin: [0,0,0,1] },
                ],
                [
                  { text: statTempMedia !== '-' ? `${statTempMedia}°C` : '-', fontSize: 11, color: '#222', bold: true, border: [false,false,false,false], fillColor: '#fafafa', margin: [0,3,0,3] },
                  { text: statTempMax !== '-' ? `${statTempMax}°C` : '-', fontSize: 11, color: '#e53935', bold: true, border: [false,false,false,false], fillColor: '#fafafa', margin: [0,3,0,3] },
                  { text: statTempMin !== '-' ? `${statTempMin}°C` : '-', fontSize: 11, color: '#1976d2', bold: true, border: [false,false,false,false], fillColor: '#fafafa', margin: [0,3,0,3] },
                  { text: statChuvaTotal !== '-' ? `${statChuvaTotal} mm` : '-', fontSize: 11, color: '#0288d1', bold: true, border: [false,false,false,false], fillColor: '#fafafa', margin: [0,3,0,3] },
                  { text: statUmiMedia !== '-' ? `${statUmiMedia}%` : '-', fontSize: 11, color: '#43a047', bold: true, border: [false,false,false,false], fillColor: '#fafafa', margin: [0,3,0,3] },
                  { text: String(totalLeituras), fontSize: 11, color: '#666', bold: true, border: [false,false,false,false], fillColor: '#fafafa', margin: [0,3,0,3] },
                ]
              ]
            },
            layout: { hLineWidth: () => 0, vLineWidth: (i, node) => (i > 0 && i < node.table.widths.length) ? 0.5 : 0, vLineColor: () => '#e8e8e8', paddingLeft: () => 4, paddingRight: () => 4, paddingTop: () => 0, paddingBottom: () => 0 },
            margin: [0, 0, 0, 12]
          },
          // Gráfico Temperatura
          chartSectionTitle(usarAgrupamentoPorHora ? 'TEMPERATURA (°C) — por hora' : 'TEMPERATURA (°C) — por dia', '#1976d2'),
          {
            columns: [
              yAxisCol(tempTicks),
              { width: '*', stack: temMin_max_disponivel
                ? buildLineChart([
                    { values: tempMaxSerie, color: '#e53935' },
                    { values: tempMediaSerie, color: '#1976d2' },
                    { values: tempMinSerie, color: '#42a5f5' }
                  ], tempTicks)
                : buildLineChart([{ values: tempMediaSerie, color: '#1976d2' }], tempTicks)
              }
            ]
          },
          // legenda temperatura
          ...(temMin_max_disponivel ? [{
            columns: [
              { text: '', width: PAD_L },
              ...legendSquare('#e53935', 'Máx'),
              ...legendSquare('#1976d2', 'Média'),
              ...legendSquare('#42a5f5', 'Mín'),
              { text: '', width: '*' }
            ],
            margin: [0, 2, 0, 1]
          }] : [{
            columns: [
              { text: '', width: PAD_L },
              ...legendSquare('#1976d2', 'Temperatura média'),
              { text: '', width: '*' }
            ],
            margin: [0, 2, 0, 1]
          }]),
          xAxisLabels(chartLabels),

          // Gráfico Chuva
          ...(chuvaValsOk.some(v => v > 0) || Number(statChuvaTotal) > 0 ? [
            chartSectionTitle(usarAgrupamentoPorHora ? 'CHUVA (mm) — por hora' : 'CHUVA ACUMULADA (mm) — por dia', '#0288d1'),
            {
              columns: [
                yAxisCol(chuvaTicks, 1),
                { width: '*', stack: buildBarChart(chuvaSerieEfetiva, '#0288d1', chuvaTicks) }
              ]
            },
            xAxisLabels(chuvaValsOk.some(v => v > 0) ? chartLabels : data.map(d => { const s=(d.data_local||'').substring(0,10); const p=s.split('-'); return p.length===3?`${p[2]}/${p[1]}`:s; })),
          ] : []),

          // Gráfico Umidade
          ...(umiValsOk.length ? [
            chartSectionTitle(usarAgrupamentoPorHora ? 'UMIDADE RELATIVA (%) — por hora' : 'UMIDADE RELATIVA (%) — por dia', '#43a047'),
            {
              columns: [
                yAxisCol(umiTicks, 0),
                { width: '*', stack: buildLineChart([{ values: umiSerie, color: '#43a047' }], umiTicks) }
              ]
            },
            { columns: [{ text: '', width: PAD_L }, ...legendSquare('#43a047', 'Umidade relativa'), { text: '', width: '*' }], margin: [0, 2, 0, 1] },
            xAxisLabels(chartLabels),
          ] : []),

          // Linha separadora antes dos insights
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 535, y2: 0, lineWidth: 1, lineColor: '#e0e0e0' }], margin: [0, 6, 0, 8] },

          // Seção de Insights
          { text: 'INSIGHTS DO PERÍODO', color: '#666', fontSize: 9, bold: true, margin: [0, 0, 0, 6] },
          {
            ul: insights.map(ins => ({
              text: ins.text,
              fontSize: 8,
              color: '#333',
              margin: [0, 0, 0, 4]
            }))
          }
        ],
        footer: function(currentPage, pageCount) {
          return {
            text: [
              { text: 'Gerado em ', fontSize: 8, color: '#999' },
              { text: new Date().toLocaleString('pt-BR'), fontSize: 8, color: '#666', bold: true },
              { text: ' • ClimoraX', fontSize: 8, color: '#003B73', bold: true }
            ],
            alignment: 'center',
            margin: [0, 15, 0, 15]
          };
        }
      };

      const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
      const nomeSensor = (sensorSelecionado.nome_sensor || sensorSelecionado.device_id).replace(/[^a-zA-Z0-9_-]/g, '_');
      const nomeArquivo = `relatorio_sensor_${nomeSensor}_${dataAtual}.pdf`;

      let isCapacitor = false;
      try {
        isCapacitor = typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
      } catch (e) { isCapacitor = false; }

      if (isCapacitor && Filesystem && Directory) {
        pdfMake.createPdf(docDefinition).getBase64(async (pdfBase64) => {
          try {
            const result = await Filesystem.writeFile({
              path: nomeArquivo,
              data: pdfBase64,
              directory: Directory.Documents,
            });
            if (FileOpener && result.uri) {
              try {
                await FileOpener.open({ filePath: result.uri, contentType: 'application/pdf' });
              } catch (e) {}
            }
            toast.success('PDF salvo em Documentos!');
            setReportFeedback({ type: 'success', message: `Relatório ${nomeArquivo} salvo em Documentos.` });
          } catch (err) {
            toast.error('Erro ao salvar PDF: ' + (err?.message || err));
            setReportFeedback({ type: 'error', message: `Não foi possível salvar o relatório: ${err?.message || err}` });
          }
          setGerandoPDF(false);
        });
      } else {
        const pdfBlob = await new Promise((resolve, reject) => {
          try {
            pdfMake.createPdf(docDefinition).getBlob(resolve);
          } catch (error) {
            reject(error);
          }
        });
        saveAs(pdfBlob, nomeArquivo);
        toast.success(`Relatório ${nomeArquivo} preparado para download.`);
        setReportFeedback({ type: 'success', message: `Download iniciado: ${nomeArquivo}` });
        setGerandoPDF(false);
      }
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      toast.error('Erro ao gerar PDF: ' + (err?.message || err));
      setReportFeedback({ type: 'error', message: `Não foi possível gerar o relatório: ${err?.message || err}` });
      setGerandoPDF(false);
    }
  }

  // Função para exportar XLSX
  async function exportarXLSX() {
    if (!sensorSelecionado || !sensorSelecionado.device_id) return;
    if (!dataIni || !dataFim) return toast.warning('Selecione o período!');
    
    // Verifica se pode fazer download (planos premium/pro)
    if (!podeUsar('download_previsao')) {
      setUpgradeRecurso('download');
      setShowUpgradeModal(true);
      return;
    }
    
    setExportando(true);
    // Consulta dados agregados diários da view
    const { data, error } = await supabase
      .from('dados_sensores_diarios')
      .select('*')
      .eq('device_id', sensorSelecionado.device_id)
      .gte('data_local', dataIni)
      .lte('data_local', dataFim)
      .order('data_local', { ascending: true });
    if (error) {
      setExportando(false);
      return toast.error('Erro ao buscar dados: ' + error.message);
    }
    if (!data || !data.length) {
      setExportando(false);
      return toast.warning('Nenhum dado encontrado no período.');
    }
    // Cabeçalho personalizado
    const cabecalho = [
      ['Relatório Diário de Sensor Meteorológico'],
      [`Sensor: ${sensorSelecionado.nome_sensor || sensorSelecionado.device_id}`],
      [`Device ID: ${sensorSelecionado.device_id}`],
      [`Período: ${dataIni} até ${dataFim}`],
      [''],
    ];
    // Dados para XLSX
    const dadosXLSX = [...cabecalho, Object.keys(data[0]), ...data.map(obj => Object.values(obj))];
    // Monta planilha
    const ws = XLSX.utils.aoa_to_sheet(dadosXLSX);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'DadosDiariosSensor');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const fileName = `${sensorSelecionado.device_id}_diario_${dataIni}_${dataFim}.xlsx`;

    // Detecta se está rodando em ambiente Capacitor (Android/iOS)
    let isCapacitor = false;
    try {
      isCapacitor = window && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
    } catch (e) {
      isCapacitor = false;
    }
    if (isCapacitor && Filesystem && Directory) {
      try {
        // Converte array buffer para base64
        function arrayBufferToBase64(buffer) {
          let binary = '';
          const bytes = new Uint8Array(buffer);
          const len = bytes.byteLength;
          for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          return window.btoa(binary);
        }
        const xlsxBase64 = arrayBufferToBase64(wbout);
        await Filesystem.writeFile({
          path: fileName,
          data: xlsxBase64,
          directory: Directory.Documents,
        });
        toast.success('XLSX salvo em Documentos!');
        console.log('XLSX salvo com sucesso no dispositivo.');
      } catch (err) {
        console.error('Erro ao salvar XLSX no dispositivo:', err);
        toast.error('Erro ao salvar XLSX: ' + (err && err.message ? err.message : err));
      }
    } else {
      try {
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        saveAs(blob, fileName);
        toast.success('XLSX baixado com sucesso!');
        console.log('XLSX baixado normalmente no navegador.');
      } catch (err) {
        console.error('Erro ao baixar XLSX no navegador:', err);
        toast.error('Erro ao baixar XLSX: ' + (err && err.message ? err.message : err));
      }
    }
    setExportando(false);
  }
  const [deviceId, setDeviceId] = useState('');
  const [nomeSensor, setNomeSensor] = useState('');
  const [latitudeCadastro, setLatitudeCadastro] = useState('');
  const [longitudeCadastro, setLongitudeCadastro] = useState('');
  const [sensorLocalizacaoMapa, setSensorLocalizacaoMapa] = useState(null);
  const [salvandoLocalizacao, setSalvandoLocalizacao] = useState(false);
  const handledInitialSensorRef = useRef('');
  // Tipo do sensor sendo cadastrado e filtro de tipo aplicado à lista
  const [tipoSensorCadastro, setTipoSensorCadastro] = useState('meteorologico');
  const [profundidadeCadastro, setProfundidadeCadastro] = useState('');
  const [filtroTipoSensor, setFiltroTipoSensor] = useState('todos');
  const [sensores, setSensores] = useState(() => previewData?.sensores || []);
  const [sensorProperties, setSensorProperties] = useState(() => previewData?.propriedades || []);
  const [loading, setLoading] = useState(!previewMode && Boolean(user?.id));
  const [telemetryLoading, setTelemetryLoading] = useState(!previewMode && Boolean(user?.id));
  // Estado para armazenar dados recentes de cada sensor
  const [dadosSensores, setDadosSensores] = useState(() => previewData?.dadosSensores || {});
  // Estado para modal de gráficos
  const [modalAberto, setModalAberto] = useState(false);
  const [sensorSelecionado, setSensorSelecionado] = useState(null);
  // Estado para exibir/ocultar formulário de cadastro
  const [mostrarCadastro, setMostrarCadastro] = useState(false);
  // Estado para confirmação de exclusão
  const [confirmarExclusao, setConfirmarExclusao] = useState({ aberto: false, id: null, nome: '' });

  useEffect(() => {
    if (!previewMode) return;
    setSensores(previewData?.sensores || []);
    setDadosSensores(previewData?.dadosSensores || {});
    setLoading(false);
    setTelemetryLoading(false);
    setMostrarCadastro(false);
    if (!initialContext?.sensorId && !initialContext?.deviceId) {
      setModalAberto(false);
      setSensorSelecionado(null);
    }
  }, [previewMode, previewData, initialContext]);

  // Estado para estiagem agrícola
  const [diasEstiagem, setDiasEstiagem] = useState(null);
  const [loadingEstiagem, setLoadingEstiagem] = useState(false);


  // Buscar dados do período selecionado apenas quando necessário (sob demanda)
  const buscarDadosPeriodo = async (deviceId, dataInicio, dataFim, tipoSensor, periodo = 'personalizado') => {
    const deviceIdLower = (deviceId || '').trim().toLowerCase();
    const tabela = getTabelaDados(tipoSensor);

    const intervalo = resolveSensorHistoryRange(periodo, dataInicio, dataFim);
    if (!intervalo) return [];

    // Para 24h usa uma janela móvel exata. Nos demais períodos mantém os dias
    // completos escolhidos pelo usuário, sempre com o offset local na consulta.
    const timestampInicio = toISOLocal(intervalo.start);
    const timestampFim = toISOLocal(intervalo.end);
    
    console.log(`🔍 Buscando dados de ${deviceIdLower} entre ${timestampInicio} e ${timestampFim}`);
    
    // Buscar com paginação apenas os dados do período
    let allData = [];
    let offset = 0;
    const batchSize = 1000;
    let hasMore = true;
    
    while (hasMore) {
      const { data, error } = await supabase
        .from(tabela)
        .select('*')
        .eq('device_id', deviceIdLower)
        .gte('timestamp', timestampInicio)
        .lte('timestamp', timestampFim)
        .order('timestamp', { ascending: false })
        .range(offset, offset + batchSize - 1);
      
      if (error) {
        console.error(`❌ Erro ao buscar dados:`, error);
        break;
      }
      
      if (data && data.length > 0) {
        allData = allData.concat(data);
        offset += batchSize;
        hasMore = data.length === batchSize;
      } else {
        hasMore = false;
      }
    }
    
    console.log(`📥 Total carregado para ${deviceIdLower}:`, allData.length, 'registros');
    return allData;
  };

  // Buscar dados das últimas 24h para cada sensor (inicial)
  useEffect(() => {
    if (previewMode) {
      setDadosSensores(previewData?.dadosSensores || {});
      setTelemetryLoading(false);
      return;
    }
    if (!sensores.length) {
      if (!loading) setTelemetryLoading(false);
      return;
    }
    let cancelled = false;
    async function fetchDados() {
      setTelemetryLoading(true);
      const agora = new Date();
      const h24Antes = new Date(agora.getTime() - 24 * 60 * 60 * 1000);

      const entries = await Promise.all(sensores.map(async sensor => {
        if (sensor.tipo_sensor === 'camera') return [sensor.device_id, []];
        const deviceId = (sensor.device_id || '').trim().toLowerCase();
        const { data, error } = await supabase
          .from(getTabelaDados(sensor.tipo_sensor))
          .select('*')
          .eq('device_id', deviceId)
          .gte('timestamp', h24Antes.toISOString())
          .order('timestamp', { ascending: false })
          .limit(1000);

        return [sensor.device_id, !error && data ? data : []];
      }));
      if (!cancelled) {
        setDadosSensores(Object.fromEntries(entries));
        setTelemetryLoading(false);
      }
    }
    fetchDados();
    return () => { cancelled = true; };
  }, [sensores, loading, previewMode, previewData]);

  // O modal sempre consulta o período ativo. A carga geral dos cards pode ainda
  // estar em andamento quando o usuário abre uma estação, portanto ela não pode
  // ser considerada a fonte obrigatória das primeiras 24 horas.
  useEffect(() => {
    if (previewMode || !modalAberto) {
      setCarregandoDadosPeriodo(false);
      return;
    }
    if (!sensorSelecionado || !dataIni || !dataFim) return;
    if (sensorSelecionado.tipo_sensor === 'camera') {
      setCarregandoDadosPeriodo(false);
      return;
    }
    
    let isCancelled = false;
    
    async function carregarDadosPeriodo() {
      setCarregandoDadosPeriodo(true);
      console.log('⏳ Carregando dados do período para:', sensorSelecionado.device_id, 'de', dataIni, 'até', dataFim);
      try {
        const dados = await buscarDadosPeriodo(
          sensorSelecionado.device_id,
          dataIni,
          dataFim,
          sensorSelecionado.tipo_sensor,
          periodoSelecionado
        );

        if (!isCancelled) {
          console.log('✅ Dados carregados:', dados.length);
          setSensorSelecionado(prev => ({ ...prev, dados }));
          setDadosSensores(prev => ({ ...prev, [sensorSelecionado.device_id]: dados }));
        }
      } finally {
        if (!isCancelled) setCarregandoDadosPeriodo(false);
      }
    }
    
    carregarDadosPeriodo();
    
    return () => {
      isCancelled = true;
    };
  }, [sensorSelecionado?.device_id, dataIni, dataFim, periodoSelecionado, previewMode, modalAberto]);

  // Buscar sensores do usuário
  useEffect(() => {
    if (previewMode) {
      setSensores(previewData?.sensores || []);
      setDadosSensores(previewData?.dadosSensores || {});
      setLoading(false);
      setTelemetryLoading(false);
      return;
    }
    if (!user?.id) {
      setTelemetryLoading(false);
      return;
    }
    async function fetchSensores() {
      setLoading(true);
      setTelemetryLoading(true);
      const { data, error } = await supabase
        .from('sensores_usuarios')
        .select('*')
        .eq('user_id', user.id)
        .order('data_cadastro', { ascending: false });
      if (!error) setSensores(data || []);
      setLoading(false);
    }
    fetchSensores();
  }, [user, previewMode, previewData]);

  useEffect(() => {
    if (previewMode) {
      setSensorProperties(previewData?.propriedades || []);
      return;
    }
    if (!user?.id) {
      setSensorProperties([]);
      return;
    }

    let active = true;
    // Usa a mesma filtragem de "propriedades visíveis" da tela Propriedades,
    // excluindo as arquivadas (apenas .from('propriedades') devolveria também
    // propriedades removidas pelo usuário, que continuam na tabela legada).
    loadVisibleAuthorizedProperties(user.id, 'id,nome,municipio,estado,coordenada_x,coordenada_y,geometria', 'propriedades')
      .then(({ properties }) => {
        if (!active) return;
        setSensorProperties(properties || []);
      })
      .catch(error => {
        if (!active) return;
        console.warn('Não foi possível validar a localização dos sensores:', error?.message);
        setSensorProperties([]);
      });

    return () => { active = false; };
  }, [user?.id, previewMode, previewData]);

  // Carregar dias de estiagem quando modal abre (apenas sensores meteorológicos)
  useEffect(() => {
    const ehMeteorologico = (sensorSelecionado?.tipo_sensor || 'meteorologico') === 'meteorologico';
    if (!sensorSelecionado?.device_id || !modalAberto || !ehMeteorologico) { setDiasEstiagem(null); return; }
    if (previewMode) {
      setDiasEstiagem(previewData?.diasEstiagem ?? 0);
      setLoadingEstiagem(false);
      return;
    }
    setLoadingEstiagem(true);
    const deviceId = (sensorSelecionado.device_id || '').trim().toLowerCase();
    // Janela ampla (não os últimos 45 dias) — uma estiagem real pode durar mais que isso,
    // e uma janela curta trava a contagem no seu próprio tamanho.
    const inicioJanela = localDateStr(new Date(Date.now() - 400 * 24 * 60 * 60 * 1000));

    Promise.all([
      supabase
        .from('dados_sensores_diarios')
        .select('data_local, chuva_total, leituras')
        .eq('device_id', deviceId)
        .gte('data_local', inicioJanela)
        .gt('leituras', 0)
        .order('data_local', { ascending: false })
        .limit(400),
      // primeira leitura real para evitar contar dias antes do sensor existir
      supabase
        .from('dados_sensores_meteorologicos')
        .select('timestamp')
        .eq('device_id', deviceId)
        .order('timestamp', { ascending: true })
        .limit(1),
    ]).then(([{ data }, { data: firstRow }]) => {
      if (!data || data.length === 0) { setDiasEstiagem(0); setLoadingEstiagem(false); return; }
      const dataInicio = firstRow?.[0]?.timestamp?.substring(0, 10) ?? inicioJanela;
      const totais = data
        .filter(r => r.data_local >= dataInicio)
        .map(r => ({ data: r.data_local, total: Number(r.chuva_total) || 0 }));
      setDiasEstiagem(calcularDiasEstiagem(totais));
      setLoadingEstiagem(false);
    });
  }, [sensorSelecionado?.device_id, modalAberto, previewMode, previewData]);

  // Cadastrar sensor no Supabase
  const excluirSensor = async (id) => {
    if (previewMode) return;
    setLoading(true);
    const { error } = await supabase
      .from('sensores_usuarios')
      .delete()
      .eq('id', id);
    if (error) {
      toast.error('Erro ao excluir sensor: ' + error.message);
    } else {
      setSensores(sensores.filter(s => s.id !== id));
      toast.success('Sensor excluído com sucesso!');
    }
    setLoading(false);
    setConfirmarExclusao({ aberto: false, id: null, nome: '' });
  };

  const cadastrarSensor = async () => {
    if (previewMode) return;
    if (!deviceId) return;
    const latitude = Number(latitudeCadastro);
    const longitude = Number(longitudeCadastro);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      toast.warning('Selecione no mapa o local onde o sensor está instalado.');
      return;
    }
    
    // Verifica limite de sensores antes de cadastrar
    const resultado = await verificarLimite('sensores');
    if (!resultado.permitido) {
      setUpgradeRecurso('sensores');
      setShowUpgradeModal(true);
      toast.warning(resultado.mensagem);
      return;
    }
    
    setLoading(true);
    const deviceIdPad = deviceId.trim().toLowerCase();
    // Verifica se o device_id está cadastrado na tabela de dispositivos provisionados
    const { data: dispositivos, error: erroBusca } = await supabase
      .from('device_id_cadastrados')
      .select('device_id')
      .eq('device_id', deviceIdPad)
      .limit(1);
    if (erroBusca) {
      toast.error('Erro ao validar device_id: ' + erroBusca.message);
      setLoading(false);
      return;
    }
    if (!dispositivos || dispositivos.length === 0) {
      toast.warning('Device ID não cadastrado. Solicite o provisionamento do dispositivo.');
      setLoading(false);
      return;
    }
    // Só cadastra se device_id for válido
    const { error } = await supabase
      .from('sensores_usuarios')
      .insert({
        user_id: user.id,
        device_id: deviceIdPad,
        nome_sensor: nomeSensor || `Sensor ${deviceIdPad}`,
        tipo_sensor: tipoSensorCadastro,
        profundidade_cm: tipoSensorCadastro === 'umidade_solo' && profundidadeCadastro ? parseFloat(profundidadeCadastro) : null,
        latitude,
        longitude,
        localizacao_atualizada_em: new Date().toISOString()
      });
    
    if (error) {
      toast.error('Erro ao cadastrar sensor: ' + error.message);
      setLoading(false);
      return;
    }
    
    toast.success('Sensor cadastrado com sucesso!');
    setDeviceId('');
    setNomeSensor('');
    setProfundidadeCadastro('');
    setLatitudeCadastro('');
    setLongitudeCadastro('');
    setMostrarCadastro(false);
    
    // Atualiza lista
    const { data } = await supabase
      .from('sensores_usuarios')
      .select('*')
      .eq('user_id', user.id)
      .order('data_cadastro', { ascending: false });
    setSensores(data || []);
    setLoading(false);
  };

  // Detecta se está rodando em ambiente Capacitor (Android/iOS)
  let isCapacitor = false;
  try {
    isCapacitor = window && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
  } catch {
    isCapacitor = false;
  }

  // Detecta se é tela grande para ajustar layout
  // Lista de sensores filtrada pelo tipo selecionado nas pills
  const sensoresFiltrados = filtroTipoSensor === 'todos'
    ? sensores
    : sensores.filter(s => (s.tipo_sensor || 'meteorologico') === filtroTipoSensor);

  const abrirDetalhesSensor = (sensor, dados) => {
    const agora = new Date();
    const antes = new Date(agora.getTime() - 24 * 60 * 60 * 1000);
    setDataIni(localDateStr(antes));
    setDataFim(localDateStr(agora));
    setPeriodoSelecionado('24h');
    setCarregandoDadosPeriodo(!previewMode);
    setReportFeedback(null);
    setSensorSelecionado({ ...sensor, dados });
    setModalAberto(true);
  };

  useEffect(() => {
    const contextKey = initialContext?.sensorId || initialContext?.deviceId || '';
    if (!contextKey || handledInitialSensorRef.current === String(contextKey) || !sensores.length) return;
    const sensor = sensores.find(item => (
      String(item.id) === String(initialContext?.sensorId)
      || String(item.device_id).toLowerCase() === String(initialContext?.deviceId || '').toLowerCase()
    ));
    if (!sensor) return;
    handledInitialSensorRef.current = String(contextKey);
    abrirDetalhesSensor(sensor, dadosSensores[sensor.device_id] || []);
  }, [initialContext, sensores, dadosSensores]);

  const selecionarLocalizacaoNoMapa = async ({ lat, lng }) => {
    if (sensorLocalizacaoMapa?.mode === 'new') {
      setLatitudeCadastro(String(lat));
      setLongitudeCadastro(String(lng));
      setSensorLocalizacaoMapa(null);
      toast.success('Local de instalação selecionado.');
      return;
    }

    const sensor = sensorLocalizacaoMapa?.sensor;
    if (!sensor?.id) return;
    setSalvandoLocalizacao(true);

    if (previewMode) {
      setSensores(current => current.map(item => item.id === sensor.id
        ? { ...item, latitude: lat, longitude: lng, localizacao_atualizada_em: new Date().toISOString() }
        : item));
      setSensorLocalizacaoMapa(null);
      setSalvandoLocalizacao(false);
      toast.success('Localização atualizada na prévia local.');
      return;
    }

    const { error } = await supabase
      .from('sensores_usuarios')
      .update({
        latitude: lat,
        longitude: lng,
        localizacao_atualizada_em: new Date().toISOString(),
      })
      .eq('id', sensor.id)
      .eq('user_id', user.id);

    if (error) {
      toast.error('Não foi possível atualizar a localização do sensor.');
      setSalvandoLocalizacao(false);
      return;
    }

    setSensores(current => current.map(item => item.id === sensor.id
      ? { ...item, latitude: lat, longitude: lng, localizacao_atualizada_em: new Date().toISOString() }
      : item));
    setSensorLocalizacaoMapa(null);
    setSalvandoLocalizacao(false);
    toast.success('Localização do sensor atualizada.');
  };

  const resumoRede = sensores.reduce((summary, sensor) => {
    const readings = dadosSensores[sensor.device_id] || [];
    const telemetry = summarizeSensorTelemetry({ ...sensor, dados: readings });
    const status = getSensorConnectionStatus(telemetry.latestDate);
    const leiturasHoje = readings.filter(reading => isReadingFromToday(reading)).length;

    summary.leiturasHoje += leiturasHoje;
    if (status.tone === 'online') summary.atualizados += 1;
    if (telemetry.latestDate && (!summary.ultimaLeitura || telemetry.latestDate > summary.ultimaLeitura)) {
      summary.ultimaLeitura = telemetry.latestDate;
    }
    return summary;
  }, { atualizados: 0, leiturasHoje: 0, ultimaLeitura: null });

  return (
    <div className={`module-shell module-shell-sensores ${darkMode ? 'is-dark' : ''}`} style={{
      paddingTop: `calc(env(safe-area-inset-top, 0px) + ${isMobile ? '12px' : '24px'})`,
      paddingLeft: `calc(env(safe-area-inset-left, 0px) + ${isMobile ? '12px' : '24px'})`,
      paddingRight: `calc(env(safe-area-inset-right, 0px) + ${isMobile ? '12px' : '24px'})`,
      paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${isMobile ? '80px' : '24px'})`,
      background: 'transparent',
      minHeight: '100vh',
      fontFamily: 'var(--font-body)'
    }}>
      {/* Header */}
      <div className="module-topbar" style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        marginBottom: 16,
        marginLeft: `calc(-1 * (env(safe-area-inset-left, 0px) + ${isMobile ? '12px' : '24px'}))`,
        marginRight: `calc(-1 * (env(safe-area-inset-right, 0px) + ${isMobile ? '12px' : '24px'}))`,
        paddingLeft: `calc(env(safe-area-inset-left, 0px) + ${isMobile ? '12px' : '24px'})`,
        paddingRight: `calc(env(safe-area-inset-right, 0px) + ${isMobile ? '12px' : '24px'})`,
        paddingTop: isMobile ? 10 : 14,
        paddingBottom: isMobile ? 10 : 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14 }}>
          <button
            onClick={onBack}
            style={{
              flexShrink: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              padding: '6px 4px',
              minWidth: 44,
              minHeight: 44,
              color: darkMode ? '#60a5fa' : '#0284c7',
              fontSize: isMobile ? 13 : 14,
              fontWeight: 600,
              borderRadius: 6,
              transition: 'opacity 0.15s',
              whiteSpace: 'nowrap',
              touchAction: 'manipulation'
            }}
            aria-label="Voltar ao painel"
            onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            {!isMobile && <span>Voltar</span>}
          </button>
          <div style={{ width: 1, height: isMobile ? 28 : 32, background: darkMode ? '#374151' : '#e2e8f0', flexShrink: 0 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12, minWidth: 0 }}>
            <div style={{
              width: isMobile ? 34 : 40,
              height: isMobile ? 34 : 40,
              borderRadius: 11,
              background: 'linear-gradient(135deg, #43cea2, #1976d2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? 16 : 20,
              boxShadow: '0 3px 10px rgba(67,206,162,0.30)',
              flexShrink: 0
            }}>📡</div>
            <div style={{ minWidth: 0 }}>
              <h1 className="module-title" style={{
                fontSize: isMobile ? 16 : 20,
                fontWeight: 700,
                color: darkMode ? '#f1f5f9' : '#0f172a',
                margin: 0,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>Sensores</h1>
              <p style={{
                margin: 0,
                fontSize: 11,
                color: darkMode ? '#6b7280' : '#94a3b8',
                marginTop: 2,
                whiteSpace: 'nowrap'
              }}>Monitoramento em tempo real</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content wrapper */}
        <div className="sensores-content">
  <div style={{ marginBottom: 28, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Indicador de limite de sensores */}
        {plano && plano.limites && (
          <div style={{
            marginBottom: 12,
            padding: '8px 16px',
            background: darkMode ? '#2a2a2a' : '#f0f4f8',
            borderRadius: 8,
            fontSize: 13,
            color: darkMode ? '#90caf9' : '#1976d2',
            fontWeight: 600,
            border: darkMode ? '1px solid #444' : '1px solid #e0e7ef'
          }}>
            📡 Sensores: {sensores.length} / {plano.limites.sensores === null ? '∞' : plano.limites.sensores}
            {plano.tipo === 'starter' && (
              <span style={{ color: '#ff9800', marginLeft: 8, fontSize: 11 }}>
                (Upgrade para mais sensores)
              </span>
            )}
          </div>
        )}
        
        {mostrarCadastro && (
          <form
            className="tech-panel sensores-connect-panel"
            aria-labelledby="sensor-connect-title"
            onSubmit={event => {
              event.preventDefault();
              cadastrarSensor();
            }}
            style={{
            display: 'flex', 
            flexDirection: 'column', 
            gap: 12, 
            borderRadius: 12, 
            padding: 18, 
            maxWidth: 820,
            width: '100%',
            boxSizing: 'border-box',
            boxShadow: darkMode ? '0 6px 18px rgba(0,0,0,0.28)' : '0 8px 20px rgba(9,53,97,0.09)'
          }}>
            <div className="sensores-connect-heading">
              <span>Adicionar dispositivo</span>
              <h2 id="sensor-connect-title">Conectar sensor</h2>
              <p>Informe o Device ID provisionado no equipamento e escolha como ele será identificado no ClimoraX.</p>
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 700, color: darkMode ? '#aaa' : '#666', marginBottom: 6, display: 'block' }}>
                Tipo de sensor
              </label>
              <div className="sensores-type-grid" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {TIPOS_SENSOR.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    className="sensores-type-option"
                    onClick={() => setTipoSensorCadastro(t.value)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '10px 14px',
                      borderRadius: 8,
                      border: tipoSensorCadastro === t.value ? `2px solid ${t.cor}` : (darkMode ? '1.5px solid #555' : '1.5px solid #b0b8c9'),
                      background: tipoSensorCadastro === t.value ? `${t.cor}22` : (darkMode ? '#1a1a1a' : '#fff'),
                      color: tipoSensorCadastro === t.value ? t.cor : (darkMode ? '#f0f0f0' : '#444'),
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: 'pointer',
                      flex: '1 1 auto',
                      justifyContent: 'center',
                      minHeight: 44,
                      touchAction: 'manipulation',
                      transition: 'all 0.15s'
                    }}
                  ><span>{t.icone}</span>{t.label}</button>
                ))}
              </div>
            </div>
            <div className="sensores-field-group">
              <label htmlFor="sensor-device-id">Device ID <span>obrigatório</span></label>
              <input
                id="sensor-device-id"
                type="text"
                placeholder="Ex.: cx-meteo-001"
                value={deviceId}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                onChange={e => setDeviceId(e.target.value)}
                style={{
                  padding: 14,
                  borderRadius: 8,
                  border: darkMode ? '1.5px solid #555' : '1.5px solid #b0b8c9',
                  width: '100%',
                  boxSizing: 'border-box',
                  fontSize: 16,
                  background: darkMode ? '#1a1a1a' : '#fff',
                  color: darkMode ? '#f0f0f0' : '#222',
                  outline: 'none'
                }}
              />
              <small>O código está na etiqueta ou na configuração da estação provisionada.</small>
            </div>
            <div className="sensores-field-group">
              <label htmlFor="sensor-name">Nome de identificação <span>opcional</span></label>
              <input
                id="sensor-name"
                type="text"
                placeholder="Ex.: Estação Talhão Norte"
                value={nomeSensor}
                onChange={e => setNomeSensor(e.target.value)}
                style={{ 
                  padding: 14, 
                  borderRadius: 8, 
                  border: darkMode ? '1.5px solid #555' : '1.5px solid #b0b8c9', 
                  width: '100%', 
                  boxSizing: 'border-box',
                  fontSize: 16,
                  background: darkMode ? '#1a1a1a' : '#fff',
                  color: darkMode ? '#f0f0f0' : '#222',
                  outline: 'none'
                }}
              />
            </div>
            {tipoSensorCadastro === 'umidade_solo' && (
              <div className="sensores-field-group">
                <label htmlFor="sensor-depth">Profundidade de instalação <span>opcional</span></label>
                <input
                  id="sensor-depth"
                  type="number"
                  inputMode="decimal"
                  placeholder="Profundidade em centímetros"
                  value={profundidadeCadastro}
                  onChange={e => setProfundidadeCadastro(e.target.value)}
                  style={{
                    padding: 14,
                    borderRadius: 8,
                    border: darkMode ? '1.5px solid #555' : '1.5px solid #b0b8c9',
                    width: '100%',
                    boxSizing: 'border-box',
                    fontSize: 16,
                    background: darkMode ? '#1a1a1a' : '#fff',
                    color: darkMode ? '#f0f0f0' : '#222',
                    outline: 'none'
                  }}
                />
              </div>
            )}
            <div className={`sensor-location-field ${latitudeCadastro && longitudeCadastro ? 'is-selected' : ''}`}>
              <div className="sensor-location-field__copy">
                <span className="sensor-location-field__icon"><FaMapMarkerAlt /></span>
                <div>
                  <strong>Local de instalação <small>obrigatório</small></strong>
                  <p>{latitudeCadastro && longitudeCadastro
                    ? 'Ponto geográfico definido. Este sensor aparecerá na Central de Operações.'
                    : 'Selecione no mapa o ponto exato onde a estação ou sensor foi instalado.'}</p>
                </div>
              </div>
              {latitudeCadastro && longitudeCadastro && (
                <div className="sensor-location-coordinates" aria-label="Coordenadas selecionadas">
                  <span><small>Latitude</small><strong>{formatCoordinate(latitudeCadastro)}</strong></span>
                  <span><small>Longitude</small><strong>{formatCoordinate(longitudeCadastro)}</strong></span>
                </div>
              )}
              <button
                type="button"
                className="sensor-location-map-button"
                onClick={() => setSensorLocalizacaoMapa({ mode: 'new' })}
              >
                <FaMapMarkerAlt /> {latitudeCadastro && longitudeCadastro ? 'Alterar ponto no mapa' : 'Selecionar no mapa'}
              </button>
            </div>
            <div className="sensores-form-actions" style={{ display: 'flex', gap: 10, marginTop: 2, flexDirection: !isMobile ? 'row' : 'column' }}>
              <button
                type="submit"
                disabled={previewMode || loading || !deviceId || !latitudeCadastro || !longitudeCadastro}
                style={{ 
                  padding: '14px 0', 
                  minHeight: 48,
                  borderRadius: 8, 
                  background: darkMode ? '#80cbc4' : '#43cea2', 
                  color: darkMode ? '#232526' : '#fff', 
                  fontWeight: 800, 
                  border: 'none', 
                   cursor: previewMode || loading || !deviceId || !latitudeCadastro || !longitudeCadastro ? 'not-allowed' : 'pointer',
                  fontSize: 17, 
                  width: '100%',
                   opacity: previewMode || loading || !deviceId || !latitudeCadastro || !longitudeCadastro ? 0.58 : 1,
                  touchAction: 'manipulation',
                  transition: 'all 0.2s'
                }}
              >{previewMode ? 'Conexão desativada na prévia' : (loading ? 'Conectando...' : 'Conectar sensor')}</button>
              <button
                type="button"
                onClick={() => {
                  setMostrarCadastro(false);
                  setLatitudeCadastro('');
                  setLongitudeCadastro('');
                }}
                style={{ 
                  padding: '14px 0', 
                  minHeight: 48,
                  borderRadius: 8, 
                  background: darkMode ? '#3a3a3a' : '#eee', 
                  color: darkMode ? '#90caf9' : '#1976d2', 
                  fontWeight: 800, 
                  border: 'none', 
                  cursor: 'pointer', 
                  fontSize: 17, 
                  width: '100%',
                  touchAction: 'manipulation',
                  transition: 'all 0.2s'
                }}
              >Cancelar</button>
            </div>
          </form>
        )}
      </div>

  {loading && !mostrarCadastro && sensores.length === 0 && (
    <div className="sensores-loading-grid" aria-label="Carregando sensores">
      <LoadingSkeleton darkMode={darkMode} />
      <LoadingSkeleton darkMode={darkMode} />
    </div>
  )}

  {!mostrarCadastro && !loading && sensores.length === 0 && (
    <section className="sensores-empty-state" aria-labelledby="sensores-empty-title">
      <div className="sensores-empty-main">
        <span className="sensores-empty-kicker">Monitoramento de campo</span>
        <div className="sensores-empty-icon" aria-hidden="true"><FaWifi /></div>
        <h2 id="sensores-empty-title">Conecte sua primeira estação</h2>
        <p>
          Vincule uma estação ou sensor fabricado pela ClimoraX para acompanhar dados do campo,
          identificar mudanças e criar alertas para a sua operação.
        </p>
        <button type="button" className="sensores-empty-primary" onClick={() => setMostrarCadastro(true)}>
          <FaPlus aria-hidden="true" />
          Conectar primeiro sensor
          <FaArrowRight aria-hidden="true" />
        </button>
        <span className="sensores-empty-note"><FaCheckCircle aria-hidden="true" /> Tenha em mãos o Device ID provisionado no equipamento.</span>

        <div className="sensores-empty-types" aria-label="Tipos de sensores aceitos">
          <div><span><FaCloudSun aria-hidden="true" /></span><strong>Meteorológico</strong></div>
          <div><span><FaSeedling aria-hidden="true" /></span><strong>Umidade do solo</strong></div>
          <div><span><FaTint aria-hidden="true" /></span><strong>Chuva e irrigação</strong></div>
        </div>
      </div>

      <aside className="sensores-empty-steps" aria-label="Como conectar um sensor">
        <span>Conexão rápida</span>
        <h3>Comece em três passos</h3>
        <ol>
          <li>
            <span><FaMicrochip aria-hidden="true" /></span>
            <div><strong>Localize o Device ID</strong><p>Use o código da etiqueta ou da configuração do dispositivo.</p></div>
          </li>
          <li>
            <span>2</span>
            <div><strong>Escolha o tipo e o nome</strong><p>Organize estações e sensores de acordo com a instalação em campo.</p></div>
          </li>
          <li>
            <span>3</span>
            <div><strong>Acompanhe as leituras</strong><p>Consulte dados recentes, períodos, gráficos, relatórios e alertas.</p></div>
          </li>
        </ol>
      </aside>
    </section>
  )}

  {sensores.length > 0 && !mostrarCadastro && (
    telemetryLoading ? (
      <section className="sensor-readings-loading" role="status" aria-live="polite">
        <div><FaClock /><span><strong>Carregando leituras</strong><small>Consultando a telemetria mais recente das estações.</small></span></div>
        <div className="sensores-loading-grid" aria-hidden="true">
          <LoadingSkeleton darkMode={darkMode} />
          <LoadingSkeleton darkMode={darkMode} />
        </div>
      </section>
    ) : (
    <>
      <section className="sensors-network-overview" aria-labelledby="sensor-network-title">
        <div className="sensors-network-overview__intro">
          <span>Rede de monitoramento</span>
          <h2 id="sensor-network-title">Situação das estações</h2>
          <p>Visão operacional baseada nas últimas leituras recebidas dos equipamentos em campo.</p>
        </div>
        <div className="sensors-network-stats">
          <article>
            <span><FaBroadcastTower /></span>
            <div><strong>{sensores.length}</strong><small>estações cadastradas</small></div>
          </article>
          <article className="is-green">
            <span><FaWifi /></span>
            <div><strong>{resumoRede.atualizados}</strong><small>atualizadas na última hora</small></div>
          </article>
          <article className="is-blue">
            <span><FaDatabase /></span>
            <div><strong>{resumoRede.leiturasHoje.toLocaleString('pt-BR')}</strong><small>leituras recebidas hoje</small></div>
          </article>
          <article className="is-teal">
            <span><FaClock /></span>
            <div><strong>{resumoRede.ultimaLeitura ? resumoRede.ultimaLeitura.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '—'}</strong><small>{resumoRede.ultimaLeitura ? 'última sincronização' : 'sem sincronização'}</small></div>
          </article>
        </div>
      </section>

      <section className="sensors-list-toolbar" aria-label="Filtros e ações dos sensores">
        <div>
          <span>Monitoramento operacional</span>
          <h2>Estações em campo</h2>
          <p>{sensoresFiltrados.length} {sensoresFiltrados.length === 1 ? 'estação encontrada' : 'estações encontradas'}</p>
        </div>
        <div className="sensors-list-toolbar__actions">
          <label htmlFor="sensor-type-filter">
            <span>Tipo de sensor</span>
            <select
              id="sensor-type-filter"
              className="tech-select sensors-type-filter"
              value={filtroTipoSensor}
              onChange={event => setFiltroTipoSensor(event.target.value)}
            >
              <option value="todos">Todos os tipos ({sensores.length})</option>
              {TIPOS_SENSOR.map(type => {
                const quantity = sensores.filter(sensor => (sensor.tipo_sensor || 'meteorologico') === type.value).length;
                return <option key={type.value} value={type.value}>{type.icone} {type.label} ({quantity})</option>;
              })}
            </select>
          </label>
          <button type="button" className="sensors-add-button" onClick={() => setMostrarCadastro(true)}>
            <FaPlus /> Cadastrar estação
          </button>
        </div>
      </section>

      <ul className="sensores-grid">
        {loading && (
          <>
            <LoadingSkeleton darkMode={darkMode} />
            <LoadingSkeleton darkMode={darkMode} />
          </>
        )}
        {!loading && sensoresFiltrados.length === 0 && (
          <li className="sensors-no-results">
            <FaBroadcastTower />
            <strong>Nenhuma estação neste filtro</strong>
            <span>Escolha outro tipo para visualizar os sensores cadastrados.</span>
          </li>
        )}
        {sensoresFiltrados.map((sensor, index) => {
          const dados = dadosSensores[sensor.device_id] || [];
          const tipoSensor = sensor.tipo_sensor || 'meteorologico';
          const tipoInfo = getTipoSensorInfo(tipoSensor);
          const dadosHoje = dados.filter(reading => isReadingFromToday(reading));
          const telemetry = summarizeSensorTelemetry({ ...sensor, dados });
          const latest = telemetry.latest || null;
          const connection = tipoSensor === 'camera'
            ? (sensor.camera_last_seen_at
              ? getSensorConnectionStatus(new Date(sensor.camera_last_seen_at))
              : {
                  tone: 'attention',
                  label: 'Monitoramento remoto',
                  detail: 'Abra para verificar as imagens',
                  icon: <FaVideo />,
                })
            : getSensorConnectionStatus(telemetry.latestDate);
          const readingAvailable = value => getSensorReadingState({
            loading: false,
            connectionTone: connection.tone,
            value,
          }) === 'available';
          const geographicMatch = findCompatibleProperty(sensorProperties, sensor);
          const hasSensorLocation = (sensor.latitude ?? sensor.lat) != null
            && (sensor.longitude ?? sensor.lng ?? sensor.lon) != null;
          const locationTooFar = hasSensorLocation
            && geographicMatch?.validation?.code === 'too_far';
          const locationMissing = !hasSensorLocation && sensorProperties.length > 0;
          const locationNeedsAttention = locationMissing || locationTooFar;

          const values = key => getSensorNumericValues(dadosHoje, key);
          const tempValues = values('temp_c');
          const humidityValues = values('rh_perc');
          const rainValues = values('rain_mm');
          const soilValues = values('umidade_solo_perc');
          const rangeText = (items, unit) => items.length
            ? `${Math.min(...items).toFixed(1)} a ${Math.max(...items).toFixed(1)}${unit} hoje`
            : 'Sem faixa calculada hoje';
          const metricAvailable = (value, historyValues, zeroIsValid = false) => getSensorMetricState({
            loading: false,
            connectionTone: connection.tone,
            value,
            historyValues,
            zeroIsValid,
          }) === 'available';

          let metrics;
          if (tipoSensor === 'meteorologico') {
            const temperature = latest?.temp_c != null ? Number(latest.temp_c) : null;
            const humidity = latest?.rh_perc != null ? Number(latest.rh_perc) : null;
            const rainToday = rainValues.length ? rainValues.reduce((total, value) => total + value, 0) : null;
            const rainState = getSensorMetricState({
              loading: false,
              connectionTone: connection.tone,
              value: rainToday,
              historyValues: rainValues,
              zeroIsValid: true,
              waitingForReadings: dadosHoje.length === 0,
            });
            metrics = [
              {
                key: 'temperature', tone: 'blue', icon: <FaThermometerHalf />, label: 'Temperatura',
                value: metricAvailable(temperature, tempValues) ? `${temperature.toFixed(1)}°C` : 'Indisponível',
                detail: metricAvailable(temperature, tempValues) ? rangeText(tempValues, '°C') : 'Leitura não disponível',
              },
              {
                key: 'humidity', tone: 'cyan', icon: <FaTint />, label: 'Umidade relativa',
                value: metricAvailable(humidity, humidityValues) ? `${humidity.toFixed(1)}%` : 'Indisponível',
                detail: metricAvailable(humidity, humidityValues) ? rangeText(humidityValues, '%') : 'Leitura não disponível',
              },
              {
                key: 'rain', tone: 'green', icon: <FaCloudRain />, label: 'Chuva acumulada',
                value: rainState === 'available' ? `${rainToday.toFixed(1)} mm` : rainState === 'pending' ? 'Aguardando leitura' : 'Indisponível',
                detail: rainState === 'available' ? 'Total medido hoje' : rainState === 'pending' ? 'Sem leituras do novo dia' : connection.tone === 'offline' ? 'Estação sem comunicação' : 'Leitura de chuva não disponível',
              },
            ];
          } else if (tipoSensor === 'umidade_solo') {
            const soilMoisture = latest?.umidade_solo_perc != null ? Number(latest.umidade_solo_perc) : null;
            const soilTemperature = latest?.temp_solo != null ? Number(latest.temp_solo) : null;
            const depth = sensor.profundidade_cm != null ? Number(sensor.profundidade_cm) : null;
            metrics = [
              {
                key: 'soil-moisture', tone: 'green', icon: <FaSeedling />, label: 'Umidade do solo',
                value: readingAvailable(soilMoisture) ? `${soilMoisture.toFixed(1)}%` : 'Indisponível',
                detail: readingAvailable(soilMoisture) ? rangeText(soilValues, '%') : 'Estação sem comunicação',
              },
              {
                key: 'soil-temperature', tone: 'blue', icon: <FaThermometerHalf />, label: 'Temperatura do solo',
                value: readingAvailable(soilTemperature) ? `${soilTemperature.toFixed(1)}°C` : 'Indisponível',
                detail: readingAvailable(soilTemperature) ? 'Última leitura disponível' : 'Estação sem comunicação',
              },
              {
                key: 'depth', tone: 'teal', icon: <FaDatabase />, label: 'Profundidade',
                value: Number.isFinite(depth) ? `${depth.toFixed(0)} cm` : '—',
                detail: Number.isFinite(depth) ? 'Profundidade instalada' : 'Não informada',
              },
            ];
          } else if (tipoSensor === 'chuva_presenca') {
            const wetState = latest?.molhado != null ? Number(latest.molhado) : null;
            const wetReadings = dadosHoje.filter(reading => Number(reading.molhado) === 1).length;
            metrics = [
              {
                key: 'wet-state', tone: wetState === 1 ? 'blue' : 'amber',
                icon: wetState === 1 ? <FaCloudRain /> : <FaCloudSun />, label: 'Estado atual',
                value: readingAvailable(wetState) ? (wetState === 1 ? 'Molhado' : 'Seco') : 'Indisponível',
                detail: readingAvailable(wetState) ? 'Última leitura disponível' : 'Estação sem comunicação',
              },
              {
                key: 'wet-readings', tone: 'cyan', icon: <FaChartLine />, label: 'Ocorrências hoje',
                value: wetReadings.toLocaleString('pt-BR'),
                detail: `${dadosHoje.length.toLocaleString('pt-BR')} leituras no período`,
              },
            ];
          } else {
            metrics = [
              {
                key: 'camera-stream', tone: 'amber', icon: <FaVideo />, label: 'Imagens da câmera',
                value: 'Última hora',
                detail: 'Atualização automática ao abrir',
              },
            ];
          }

          return (
            <li className={`tech-sensor-card sensores-card is-${connection.tone}`} key={sensor.id || index}>
              <article className="sensor-list-card">
                <header className="sensor-list-card__header">
                  <div className="sensor-list-card__identity">
                    <span className="sensor-list-card__icon" aria-hidden="true">{tipoInfo.icone}</span>
                    <div>
                      <span>{tipoInfo.label}</span>
                      <h3>{sensor.nome_sensor || `Sensor ${sensor.device_id}`}</h3>
                      <small><FaMicrochip /> {sensor.device_id}</small>
                    </div>
                  </div>
                  <div className={`sensor-connection-badge is-${connection.tone}`}>
                    <span>{connection.icon}</span>
                    <div><strong>{connection.label}</strong><small>{connection.detail}</small></div>
                  </div>
                </header>

                <div className="sensor-list-card__metrics" aria-label="Dados atuais do sensor">
                  {metrics.map(metric => (
                    <div className={`sensor-list-metric is-${metric.tone}`} key={metric.key}>
                      <span>{metric.icon}</span>
                      <div><small>{metric.label}</small><strong>{metric.value}</strong><p>{metric.detail}</p></div>
                    </div>
                  ))}
                </div>

                <div className="sensor-list-card__facts">
                  {tipoSensor === 'camera' ? (
                    <>
                      <span><FaDatabase />Histórico circular de 1 hora</span>
                      <span><FaBroadcastTower />Quadro atual a cada 5 s</span>
                    </>
                  ) : (
                    <>
                      <span><FaDatabase /><strong>{dadosHoje.length.toLocaleString('pt-BR')}</strong> leituras hoje</span>
                      <span><FaBroadcastTower />{formatCadence(telemetry.cadenceMinutes)}</span>
                    </>
                  )}
                  {(sensor.latitude ?? sensor.lat) != null && (sensor.longitude ?? sensor.lon) != null && (
                    <span><FaMapMarkerAlt />{formatCoordinate(sensor.latitude ?? sensor.lat)}, {formatCoordinate(sensor.longitude ?? sensor.lon)}</span>
                  )}
                </div>

                {locationNeedsAttention && (
                  <div className={`sensor-location-warning${locationMissing ? '' : ' is-info'}`} role={locationMissing ? 'alert' : 'status'}>
                    <FaExclamationTriangle aria-hidden="true" />
                    <div>
                      <strong>{locationMissing ? 'Localização da estação ainda não definida' : 'Estação sem proximidade de propriedade cadastrada'}</strong>
                      {locationMissing && (
                        <span>Marque o ponto real de instalação para habilitar o vínculo geográfico e a irrigação.</span>
                      )}
                    </div>
                  </div>
                )}
                {!locationNeedsAttention && geographicMatch?.validation?.valid && (
                  <div className="sensor-location-match">
                    <FaMapMarkerAlt aria-hidden="true" /> {geographicMatch.validation.code === 'inside_property' ? 'Dentro da propriedade' : 'Próxima de'} {geographicMatch.property.nome}
                  </div>
                )}

                <footer className="sensor-list-card__footer">
                  <button type="button" className="sensor-details-button" onClick={() => abrirDetalhesSensor(sensor, dados)}>
                    {tipoSensor === 'camera' ? <><FaVideo /> Ver câmera</> : <>Ver dados e gráficos <FaArrowRight /></>}
                  </button>
                  <button
                    type="button"
                    className="sensor-location-edit-button"
                    onClick={() => setSensorLocalizacaoMapa({ mode: 'edit', sensor })}
                    title={locationMissing ? 'Definir localização no mapa' : 'Ajustar localização no mapa'}
                  >
                    <FaMapMarkerAlt /> <span>{locationMissing ? 'Definir localização' : 'Ajustar localização'}</span>
                  </button>
                  {!previewMode && (
                    <button
                      type="button"
                      className="sensores-delete-btn"
                      onClick={() => setConfirmarExclusao({ aberto: true, id: sensor.id, nome: sensor.nome_sensor || sensor.device_id })}
                      title="Excluir sensor"
                    >
                      <FaTrashAlt /> <span>Excluir</span>
                    </button>
                  )}
                </footer>
              </article>
            </li>
          );
        })}
      </ul>
    </>
    )
  )}
      {/* Modal de gráficos do sensor */}
      <Modal
        isOpen={modalAberto}
        onRequestClose={() => setModalAberto(false)}
        overlayClassName="sensores-modal-overlay"
        className={`sensores-modal${darkMode ? ' sensores-modal--dark' : ''}`}
        contentLabel={sensorSelecionado
          ? `Detalhes do sensor ${sensorSelecionado.nome_sensor || sensorSelecionado.device_id}`
          : 'Detalhes do sensor'}
        ariaHideApp={false}
      >
        <div className="sensores-modal__header">
          {sensorSelecionado && (
            <div className="sensores-modal__identity">
              <span className="sensores-modal__sensor-icon" aria-hidden="true">
                {getTipoSensorInfo(sensorSelecionado.tipo_sensor).icone}
              </span>
              <div className="sensores-modal__title-group">
                <span>Detalhes do sensor</span>
                <h2>{sensorSelecionado.nome_sensor || sensorSelecionado.device_id}</h2>
                <div className="sensores-modal__header-meta">
                  <small><FaMicrochip /> {sensorSelecionado.device_id}</small>
                  <small><FaBroadcastTower /> {getTipoSensorInfo(sensorSelecionado.tipo_sensor).label}</small>
                  {(sensorSelecionado.latitude ?? sensorSelecionado.lat) != null && (sensorSelecionado.longitude ?? sensorSelecionado.lon) != null && (
                    <small><FaMapMarkerAlt /> {formatCoordinate(sensorSelecionado.latitude ?? sensorSelecionado.lat)}, {formatCoordinate(sensorSelecionado.longitude ?? sensorSelecionado.lon)}</small>
                  )}
                </div>
              </div>
            </div>
          )}
          <button
            type="button"
            className="sensores-modal__close"
            onClick={() => setModalAberto(false)}
            aria-label="Fechar modal"
            title="Fechar detalhes do sensor"
          >×</button>
        </div>
        {sensorSelecionado?.tipo_sensor === 'camera' && (
          <CameraStreamPlayer camera={sensorSelecionado} />
        )}
        <div className="sensores-modal__body">
        {/* Loader de carregamento/exportação */}
        {(exportando || loading) && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: 120,
            gap: '1rem'
          }}>
            <div style={{
              border: '4px solid #e0e7ef',
              borderTop: '4px solid #1976d2',
              borderRadius: '50%',
              width: 40, 
              height: 40,
              animation: 'spin 1s linear infinite',
            }} />
            <span style={{ 
              color: darkMode ? '#90caf9' : '#1976d2', 
              fontWeight: 600, 
              fontSize: 16 
            }}>
              {exportando ? 'Exportando...' : 'Carregando...'}
            </span>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }`}</style>
          </div>
        )}
        <SensorTelemetryOverview
          sensor={sensorSelecionado}
          diasEstiagem={diasEstiagem}
          loadingEstiagem={loadingEstiagem}
        />
        {/* Painel de dados atuais do sensor (meteorológico) */}
        {false && sensorSelecionado && (sensorSelecionado.tipo_sensor || 'meteorologico') === 'meteorologico' && sensorSelecionado.dados && sensorSelecionado.dados.length > 0 && (() => {
          const dadoAtual = sensorSelecionado.dados[0];
          const tempAtual = dadoAtual.temp_c !== undefined && dadoAtual.temp_c !== null ? Number(dadoAtual.temp_c).toFixed(1) : '-';
          const umiAtual = dadoAtual.rh_perc !== undefined && dadoAtual.rh_perc !== null ? Number(dadoAtual.rh_perc).toFixed(1) : null;
          const chuvaAtual = dadoAtual.rain_mm !== undefined && dadoAtual.rain_mm !== null ? Number(dadoAtual.rain_mm).toFixed(1) : '-';
          return (
            <div style={{
                display: 'flex',
                gap: 8,
                alignItems: 'stretch',
                justifyContent: 'space-around',
                background: 'linear-gradient(135deg, #f7fafd 0%, #e3f2fd 100%)',
                borderRadius: 12,
                padding: '12px 8px',
                marginBottom: 12,
                boxShadow: '0 2px 12px rgba(25, 118, 210, 0.15)',
                border: '1px solid #e0e7ef',
                flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: '1 1 100px', minWidth: 90 }}>
                 <span style={{ fontSize: 24 }}>🌡️</span>
                 <span style={{ color: '#1976d2', fontWeight: 800, fontSize: 18 }}>{tempAtual}°C</span>
                 <span style={{ color: '#666', fontWeight: 600, fontSize: 11, textAlign: 'center' }}>Temperatura</span>
              </div>
              {umiAtual !== null && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: '1 1 100px', minWidth: 90 }}>
                  <span style={{ fontSize: 24 }}>💧</span>
                  <span style={{ color: '#00bcd4', fontWeight: 800, fontSize: 18 }}>{umiAtual}%</span>
                  <span style={{ color: '#666', fontWeight: 600, fontSize: 11, textAlign: 'center' }}>Umidade</span>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: '1 1 100px', minWidth: 90 }}>
                 <span style={{ fontSize: 24 }}>🌧️</span>
                 <span style={{ color: '#43cea2', fontWeight: 800, fontSize: 18 }}>{chuvaAtual} mm</span>
                 <span style={{ color: '#666', fontWeight: 600, fontSize: 11, textAlign: 'center' }}>Chuva</span>
              </div>
              {/* Estiagem Agrícola no card superior */}
              {(() => {
                if (loadingEstiagem) return (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: '1 1 100px', minWidth: 90 }}>
                    <span style={{ fontSize: 24 }}>☀️</span>
                    <span style={{ color: '#aaa', fontWeight: 800, fontSize: 14 }}>...</span>
                    <span style={{ color: '#666', fontWeight: 600, fontSize: 11, textAlign: 'center' }}>Estiagem</span>
                  </div>
                );
                if (diasEstiagem === null) return null;
                const { cor, icone } = infoCor(diasEstiagem);
                const label = diasEstiagem === 0 ? 'Sem estiagem' : `${diasEstiagem} dia${diasEstiagem > 1 ? 's' : ''}`;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: '1 1 100px', minWidth: 90,
                    borderLeft: '1.5px solid #e0e7ef', paddingLeft: 8 }}>
                    <span style={{ fontSize: 24 }}>{diasEstiagem === 0 ? '🌿' : diasEstiagem < 10 ? '🌤️' : diasEstiagem < 20 ? '⚠️' : diasEstiagem < 30 ? '🔴' : '🚨'}</span>
                    <span style={{ color: cor, fontWeight: 800, fontSize: diasEstiagem === 0 ? 15 : 18 }}>{label}</span>
                    <span style={{ color: '#666', fontWeight: 600, fontSize: 11, textAlign: 'center' }}>Estiagem Agro</span>
                  </div>
                );
              })()}
            </div>
          );
        })()}
        {/* Painel de dados atuais do sensor (umidade do solo) */}
        {false && sensorSelecionado && sensorSelecionado.tipo_sensor === 'umidade_solo' && sensorSelecionado.dados && sensorSelecionado.dados.length > 0 && (() => {
          const dadoAtual = sensorSelecionado.dados[0];
          const soloAtual = dadoAtual.umidade_solo_perc != null ? Number(dadoAtual.umidade_solo_perc).toFixed(1) : null;
          const tempSoloAtual = dadoAtual.temp_solo != null ? Number(dadoAtual.temp_solo).toFixed(1) : null;
          return (
            <div style={{
                display: 'flex', gap: 8, alignItems: 'stretch', justifyContent: 'space-around',
                background: 'linear-gradient(135deg, #f1f8f2 0%, #e3f2e6 100%)', borderRadius: 12,
                padding: '12px 8px', marginBottom: 12, boxShadow: '0 2px 12px rgba(67,160,71,0.15)',
                border: '1px solid #e0e7ef', flexWrap: 'wrap',
            }}>
              {soloAtual !== null && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: '1 1 100px', minWidth: 90 }}>
                  <span style={{ fontSize: 24 }}>🌱</span>
                  <span style={{ color: '#2e7d32', fontWeight: 800, fontSize: 18 }}>{soloAtual}%</span>
                  <span style={{ color: '#666', fontWeight: 600, fontSize: 11, textAlign: 'center' }}>Umidade do Solo</span>
                </div>
              )}
              {tempSoloAtual !== null && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: '1 1 100px', minWidth: 90 }}>
                  <span style={{ fontSize: 24 }}>🌡️</span>
                  <span style={{ color: '#1976d2', fontWeight: 800, fontSize: 18 }}>{tempSoloAtual}°C</span>
                  <span style={{ color: '#666', fontWeight: 600, fontSize: 11, textAlign: 'center' }}>Temp. do Solo</span>
                </div>
              )}
              {sensorSelecionado.profundidade_cm && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: '1 1 100px', minWidth: 90 }}>
                  <span style={{ fontSize: 24 }}>📏</span>
                  <span style={{ color: '#666', fontWeight: 800, fontSize: 18 }}>{sensorSelecionado.profundidade_cm}cm</span>
                  <span style={{ color: '#666', fontWeight: 600, fontSize: 11, textAlign: 'center' }}>Profundidade</span>
                </div>
              )}
            </div>
          );
        })()}
        {/* Painel de dados atuais do sensor (chuva/presença de água) */}
        {false && sensorSelecionado && sensorSelecionado.tipo_sensor === 'chuva_presenca' && sensorSelecionado.dados && sensorSelecionado.dados.length > 0 && (() => {
          const dadoAtual = sensorSelecionado.dados[0];
          const estadoAtual = dadoAtual.molhado != null ? Number(dadoAtual.molhado) : null;
          return (
            <div style={{
                display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center',
                background: estadoAtual === 1 ? 'linear-gradient(135deg, #e3f2fd 0%, #cfe8fb 100%)' : 'linear-gradient(135deg, #fff3e0 0%, #ffe6c2 100%)',
                borderRadius: 12, padding: '16px 8px', marginBottom: 12,
                boxShadow: '0 2px 12px rgba(2,136,209,0.15)', border: '1px solid #e0e7ef',
            }}>
              <span style={{ fontSize: 32 }}>{estadoAtual === 1 ? '🌧️' : '☀️'}</span>
              <span style={{ color: estadoAtual === 1 ? '#0288d1' : '#e65100', fontWeight: 800, fontSize: 24 }}>
                {estadoAtual === 1 ? 'Molhado' : estadoAtual === 0 ? 'Seco' : 'Sem leitura'}
              </span>
            </div>
          );
        })()}
        {/* Conteúdo detalhado do modal */}
        {sensorSelecionado && (
          <div className="sensor-modal-analysis">
            {/* Painel de seleção de período com destaque visual e pré-seleção */}
            <section className="sensor-analysis-controls">
            <div className="sensor-period-tabs" style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
              <div className="sensor-analysis-controls__heading"><span className="sensor-modal-kicker">Análise histórica</span><h3>Selecione o período</h3></div>
              <button onClick={() => {
                const hoje = new Date();
                const ontem = new Date(hoje.getTime() - 24*60*60*1000);
                setDataIni(localDateStr(ontem));
                setDataFim(localDateStr(hoje));
                setPeriodoSelecionado('24h');
              }} className={periodoSelecionado === '24h' ? 'is-active' : ''} style={{
                padding: '10px 16px',
                borderRadius: 8,
                border: periodoSelecionado === '24h' ? '2px solid #1976d2' : '1.5px solid #b0b8c9',
                background: periodoSelecionado === '24h' ? '#1976d2' : '#fff',
                color: periodoSelecionado === '24h' ? '#fff' : '#1976d2',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                boxShadow: periodoSelecionado === '24h' ? '0 2px 8px rgba(25, 118, 210, 0.3)' : 'none',
                transition: 'all 0.2s',
                flex: '1 1 auto',
                minWidth: 90,
              }}>Últimas 24h</button>
              <button onClick={() => {
                // Verifica se o plano permite período completo
                if (!podeUsar('sensores_periodo')) {
                  setUpgradeRecurso('sensores');
                  setShowUpgradeModal(true);
                  toast.warning('Períodos maiores que 24h são exclusivos dos planos Premium e Pro!');
                  return;
                }
                const hoje = new Date();
                const seteDias = new Date(hoje.getTime() - 6*24*60*60*1000);
                setDataIni(localDateStr(seteDias));
                setDataFim(localDateStr(hoje));
                setPeriodoSelecionado('7d');
              }} className={periodoSelecionado === '7d' ? 'is-active' : ''} style={{
                padding: '10px 16px',
                borderRadius: 8,
                border: periodoSelecionado === '7d' ? '2px solid #1976d2' : '1.5px solid #b0b8c9',
                background: periodoSelecionado === '7d' ? '#1976d2' : '#fff',
                color: periodoSelecionado === '7d' ? '#fff' : '#1976d2',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                boxShadow: periodoSelecionado === '7d' ? '0 2px 8px rgba(25, 118, 210, 0.3)' : 'none',
                transition: 'all 0.2s',
                flex: '1 1 auto',
                minWidth: 90,
              }}>7 dias</button>
              <button onClick={() => {
                // Verifica se o plano permite período completo
                if (!podeUsar('sensores_periodo')) {
                  setUpgradeRecurso('sensores');
                  setShowUpgradeModal(true);
                  toast.warning('Períodos maiores que 24h são exclusivos dos planos Premium e Pro!');
                  return;
                }
                const hoje = new Date();
                const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
                setDataIni(localDateStr(inicioMes));
                setDataFim(localDateStr(hoje));
                setPeriodoSelecionado('mes');
              }} className={periodoSelecionado === 'mes' ? 'is-active' : ''} style={{
                padding: '10px 16px',
                borderRadius: 8,
                border: periodoSelecionado === 'mes' ? '2px solid #1976d2' : '1.5px solid #b0b8c9',
                background: periodoSelecionado === 'mes' ? '#1976d2' : '#fff',
                color: periodoSelecionado === 'mes' ? '#fff' : '#1976d2',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                boxShadow: periodoSelecionado === 'mes' ? '0 2px 8px rgba(25, 118, 210, 0.3)' : 'none',
                transition: 'all 0.2s',
                flex: '1 1 auto',
                minWidth: 90,
              }}>Este mês</button>
            </div>
            
            {/* Seleção customizada de datas */}
            <div className="sensor-custom-period" style={{ marginBottom: 18, display: 'flex', gap: 16, alignItems: 'stretch', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, color: '#666', fontSize: 13, width: '100%', marginBottom: 4 }}>
                Personalizado: {!podeUsarSensoresPeriodo && <span style={{ color: '#ff9800', fontSize: 11, fontWeight: 700 }}>(Premium/Pro)</span>}
              </span>
              <label style={{ fontSize: 13, color: '#1976d2', fontWeight: 600, flex: '1 1 40%', minWidth: 120, display: 'flex', flexDirection: 'column', gap: 6 }}>De:
                <input 
                  type="date" 
                  value={dataIni} 
                  onChange={e => {
                    const novaData = e.target.value;
                    const diff = Math.abs(new Date(dataFim) - new Date(novaData)) / (1000 * 60 * 60 * 24);
                    if (diff > 1 && !podeUsar('sensores_periodo')) {
                      setUpgradeRecurso('sensores');
                      setShowUpgradeModal(true);
                      toast.warning('Períodos maiores que 24h são exclusivos dos planos Premium e Pro!');
                      return;
                    }
                    setDataIni(novaData);
                    setPeriodoSelecionado('custom');
                  }} 
                  style={{ padding: 8, borderRadius: 6, border: '1.5px solid #e0e7ef', fontSize: 14, width: '100%', boxSizing: 'border-box' }} 
                />
              </label>
              <label style={{ fontSize: 13, color: '#1976d2', fontWeight: 600, flex: '1 1 40%', minWidth: 120, display: 'flex', flexDirection: 'column', gap: 6 }}>Até:
                <input 
                  type="date" 
                  value={dataFim} 
                  onChange={e => {
                    const novaData = e.target.value;
                    const diff = Math.abs(new Date(novaData) - new Date(dataIni)) / (1000 * 60 * 60 * 24);
                    if (diff > 1 && !podeUsar('sensores_periodo')) {
                      setUpgradeRecurso('sensores');
                      setShowUpgradeModal(true);
                      toast.warning('Períodos maiores que 24h são exclusivos dos planos Premium e Pro!');
                      return;
                    }
                    setDataFim(novaData);
                    setPeriodoSelecionado('custom');
                  }} 
                  style={{ padding: 8, borderRadius: 6, border: '1.5px solid #e0e7ef', fontSize: 14, width: '100%', boxSizing: 'border-box' }} 
                />
              </label>
              {(sensorSelecionado.tipo_sensor || 'meteorologico') === 'meteorologico' && (
                <>
                  <button
                    className="sensor-export-button is-xlsx"
                    onClick={exportarXLSX}
                    disabled={exportando || gerandoPDF}
                    style={{ padding: '10px 20px', borderRadius: 8, background: (exportando || gerandoPDF) ? '#ccc' : '#43cea2', color: '#fff', fontWeight: 700, border: 'none', fontSize: 14, cursor: (exportando || gerandoPDF) ? 'not-allowed' : 'pointer', flex: '1 1 45%', minWidth: 140, marginTop: 4, boxShadow: (exportando || gerandoPDF) ? 'none' : '0 2px 8px rgba(67, 206, 162, 0.3)' }}
                  >{exportando ? 'Exportando...' : <><FaFileExcel /> Exportar XLSX</>}</button>
                  <button
                    className="sensor-export-button is-pdf"
                    onClick={exportarPDF}
                    disabled={exportando || gerandoPDF}
                    style={{ padding: '10px 20px', borderRadius: 8, background: (exportando || gerandoPDF) ? '#ccc' : podeUsarPdf ? '#003B73' : '#ff9800', color: '#fff', fontWeight: 700, border: 'none', fontSize: 14, cursor: (exportando || gerandoPDF) ? 'not-allowed' : 'pointer', flex: '1 1 45%', minWidth: 140, marginTop: 4, boxShadow: (exportando || gerandoPDF) ? 'none' : '0 2px 8px rgba(0,59,115,0.3)' }}
                  >{gerandoPDF ? 'Gerando PDF...' : podeUsarPdf ? <><FaFilePdf /> Exportar PDF</> : '🔒 Relatório PDF (Premium)'}</button>
                </>
              )}
            </div>
            {reportFeedback && (
              <div
                className={`sensor-report-feedback is-${reportFeedback.type}`}
                role={reportFeedback.type === 'error' ? 'alert' : 'status'}
                aria-live="polite"
              >
                {reportFeedback.message}
              </div>
            )}
            {carregandoDadosPeriodo && (
              <div className="sensor-report-feedback" role="status" aria-live="polite">
                Atualizando as leituras do período selecionado…
              </div>
            )}
            </section>

            {/* Resumo estatístico do período filtrado */}
            {(sensorSelecionado.tipo_sensor || 'meteorologico') === 'meteorologico' && sensorSelecionado.dados && sensorSelecionado.dados.length > 0 && dataIni && dataFim && (() => {
              // Para "Últimas 24h", usa timestamp exato; para outros períodos, usa data completa
              let ini, fim;
              if (periodoSelecionado === '24h') {
                fim = new Date();
                ini = new Date(fim.getTime() - 24 * 60 * 60 * 1000);
              } else {
                // Usar horário local (Brasília) para corresponder aos dados do banco
                ini = new Date(dataIni + 'T00:00:00');
                fim = new Date(dataFim + 'T23:59:59.999');
              }
              const dados = sensorSelecionado.dados.filter(d => {
                const dataStr = d.data_local || d.timestamp || d.created_at;
                if (!dataStr) return false;
                const dt = new Date(dataStr);
                return dt >= ini && dt <= fim;
              });
              // Temperatura
              const tempVals = getSensorNumericValues(dados, 'temp_c');
              const tempMedia = tempVals.length ? (tempVals.reduce((a,b) => a+b,0)/tempVals.length).toFixed(1) : '-';
              const tempMax = tempVals.length ? Math.max(...tempVals).toFixed(1) : '-';
              const tempMin = tempVals.length ? Math.min(...tempVals).toFixed(1) : '-';
              // Umidade
              const umiVals = getSensorNumericValues(dados, 'rh_perc');
              const umiMedia = umiVals.length ? (umiVals.reduce((a,b) => a+b,0)/umiVals.length).toFixed(1) : '-';
              const umiMax = umiVals.length ? Math.max(...umiVals).toFixed(1) : '-';
              const umiMin = umiVals.length ? Math.min(...umiVals).toFixed(1) : '-';
              // Chuva
              const chuvaVals = getSensorNumericValues(dados, 'rain_mm');
              const chuvaTotal = chuvaVals.length ? chuvaVals.reduce((a,b) => a+b,0).toFixed(1) : '-';
              const tempAmplitude = tempVals.length ? (Math.max(...tempVals) - Math.min(...tempVals)).toFixed(1) : '-';
              const leiturasComChuva = chuvaVals.filter(value => value > 0).length;
              const telemetry = summarizeSensorTelemetry({ ...sensorSelecionado, dados });
              return (
                <section className="sensor-period-summary" aria-label="Resumo do período selecionado">
                  <div className="sensor-section-heading">
                    <div><span className="sensor-modal-kicker">Dados consolidados</span><h3>Resumo do período</h3></div>
                    <small>{dados.length.toLocaleString('pt-BR')} leituras analisadas</small>
                  </div>
                  <div className="sensor-period-summary__grid">
                    <article className="is-temperature"><FaThermometerHalf /><div><span>Temperatura média</span><strong>{tempMedia}<small>°C</small></strong><p>{tempMin}° mín. · {tempMax}° máx. · amplitude {tempAmplitude}°</p></div></article>
                    <article className="is-humidity"><FaTint /><div><span>Umidade média</span><strong>{umiMedia}<small>%</small></strong><p>Faixa observada: {umiMin}% a {umiMax}%</p></div></article>
                    <article className="is-rain"><FaCloudRain /><div><span>Chuva acumulada</span><strong>{chuvaTotal}<small>mm</small></strong><p>{leiturasComChuva} leitura{leiturasComChuva === 1 ? '' : 's'} com precipitação</p></div></article>
                    <article className="is-coverage"><FaCheckCircle /><div><span>Cobertura estimada</span><strong>{telemetry.coverage == null ? '—' : telemetry.coverage}<small>{telemetry.coverage == null ? '' : '%'}</small></strong><p>{formatCadence(telemetry.cadenceMinutes)}</p></div></article>
                  </div>
                </section>
              );
            })()}

            {/* Gráficos das variáveis do sensor para o período selecionado */}
            {(sensorSelecionado.tipo_sensor || 'meteorologico') === 'meteorologico' && sensorSelecionado.dados && sensorSelecionado.dados.length > 0 && dataIni && dataFim ? (() => {
              // Para "Últimas 24h", usa timestamp exato; para outros períodos, usa data completa
              let ini, fim;
              if (periodoSelecionado === '24h') {
                fim = new Date();
                ini = new Date(fim.getTime() - 24 * 60 * 60 * 1000);
              } else {
                // Usar horário local (Brasília) para corresponder aos dados do banco
                ini = new Date(dataIni + 'T00:00:00');
                fim = new Date(dataFim + 'T23:59:59.999');
              }
              
              // Filtra dados do período
              const dadosPeriodo = sensorSelecionado.dados.filter(d => {
                const dataStr = d.data_local || d.timestamp || d.created_at;
                if (!dataStr) return false;
                const dt = new Date(dataStr);
                return !Number.isNaN(dt.getTime()) && dt >= ini && dt <= fim;
              });
              
              if (dadosPeriodo.length === 0) {
                return (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    background: '#f7fafd',
                    borderRadius: 12,
                    border: '2px dashed #e0e7ef',
                    color: '#888',
                    fontSize: 16,
                    fontWeight: 600
                  }}>
                    📊 Sem dados disponíveis para o período selecionado
                  </div>
                );
              }
              
              // Calcula diferença em dias
              // Número de dias completos no intervalo [ini, fim] inclusive
              const diffDias = Math.round((new Date(dataFim + 'T00:00:00') - new Date(dataIni + 'T00:00:00')) / (1000 * 60 * 60 * 24)) + 1;
              const usarAgrupamentoPorHora = diffDias <= 2; // 24h ou menos = agrupa por hora
              
              return (
              <>
                {/* Temperatura agrupada dinamicamente */}
                <div className="sensor-chart-card is-temperature" style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, color: '#1976d2', marginBottom: 10, fontSize: 15 }}>
                    Temperatura (°C) - {usarAgrupamentoPorHora ? 'por hora' : 'por dia'}
                  </div>
                  {(() => {
                    const categorias = [];
                    const tempPorPeriodo = [];
                    
                    if (usarAgrupamentoPorHora) {
                      // Agrupamento por hora
                      const totalHoras = Math.min(48, Math.ceil((fim - ini) / (1000 * 60 * 60)));
                      let ultimoDia = null;
                      for (let i = 0; i < totalHoras; i++) {
                        const hora = new Date(ini.getTime() + i * 60 * 60 * 1000);
                        const diaAtual = hora.getDate();
                        // Mostra data no primeiro item de cada dia para orientação
                        const label = (ultimoDia !== diaAtual && totalHoras > 24) 
                          ? `${hora.getDate().toString().padStart(2, '0')}/${(hora.getMonth()+1).toString().padStart(2, '0')} ${hora.getHours().toString().padStart(2, '0')}h`
                          : `${hora.getHours().toString().padStart(2, '0')}h`;
                        ultimoDia = diaAtual;
                        categorias.push(label);
                        
                        const dadosHora = dadosPeriodo.filter(d => {
                          const dataStr = d.data_local || d.timestamp || d.created_at;
                          if (!dataStr) return false;
                          const dt = new Date(dataStr);
                          return dt.getFullYear() === hora.getFullYear() &&
                            dt.getMonth() === hora.getMonth() &&
                            dt.getDate() === hora.getDate() &&
                            dt.getHours() === hora.getHours();
                        });
                        
                        const media = averageSensorValues(dadosHora, 'temp_c');
                        tempPorPeriodo.push(media === null ? null : Number(media.toFixed(2)));
                      }
                    } else {
                      // Agrupamento por dia
                      for (let i = 0; i < diffDias; i++) {
                        const dia = new Date(ini.getTime() + i * 24 * 60 * 60 * 1000);
                        const label = `${dia.getDate().toString().padStart(2, '0')}/${(dia.getMonth()+1).toString().padStart(2, '0')}`;
                        categorias.push(label);
                        
                        const dadosDia = dadosPeriodo.filter(d => {
                          const dataStr = d.data_local || d.timestamp || d.created_at;
                          if (!dataStr) return false;
                          const dt = new Date(dataStr);
                          
                          return dt.getFullYear() === dia.getFullYear() &&
                            dt.getMonth() === dia.getMonth() &&
                            dt.getDate() === dia.getDate();
                        });
                        
                        const media = averageSensorValues(dadosDia, 'temp_c');
                        tempPorPeriodo.push(media === null ? null : Number(media.toFixed(2)));
                      }
                    }
                    
                    return (
                      <ApexCharts
                        type="line"
                        height={180}
                        options={{
                          chart: { id: 'temp-c', toolbar: { show: false }, zoom: { enabled: false } },
                          xaxis: {
                            categories: categorias,
                            tickAmount: isMobile ? 6 : undefined,
                            title: { text: usarAgrupamentoPorHora ? 'Hora' : 'Dia', style: { fontSize: 12 } },
                            labels: {
                              rotate: -45,
                              rotateAlways: false,
                              hideOverlappingLabels: true,
                              maxHeight: 80,
                              style: { fontSize: '10px' },
                            },
                          },
                          yaxis: {
                            labels: { 
                              formatter: val => val !== null ? Number(val).toFixed(1) : '-',
                              style: { fontSize: '11px' }
                            }
                          },
                          stroke: { curve: 'smooth', width: 2.5 },
                          colors: ['#1976d2'],
                          tooltip: { enabled: true },
                          grid: { borderColor: '#e0e7ef' },
                        }}
                        series={[{ name: 'Temperatura média', data: tempPorPeriodo }]}
                      />
                    );
                  })()}
                </div>

                {/* Chuva agrupada dinamicamente */}
                <div className="sensor-chart-card is-rain" style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, color: '#43cea2', marginBottom: 10, fontSize: 15 }}>
                    Chuva (mm) - {usarAgrupamentoPorHora ? 'por hora' : 'por dia'}
                  </div>
                  {(() => {
                    const categorias = [];
                    const chuvaPorPeriodo = [];
                    
                    if (usarAgrupamentoPorHora) {
                      const totalHoras = Math.min(48, Math.ceil((fim - ini) / (1000 * 60 * 60)));
                      let ultimoDia = null;
                      for (let i = 0; i < totalHoras; i++) {
                        const hora = new Date(ini.getTime() + i * 60 * 60 * 1000);
                        const diaAtual = hora.getDate();
                        const label = (ultimoDia !== diaAtual && totalHoras > 24) 
                          ? `${hora.getDate().toString().padStart(2, '0')}/${(hora.getMonth()+1).toString().padStart(2, '0')} ${hora.getHours().toString().padStart(2, '0')}h`
                          : `${hora.getHours().toString().padStart(2, '0')}h`;
                        ultimoDia = diaAtual;
                        categorias.push(label);
                        
                        const dadosHora = dadosPeriodo.filter(d => {
                          const dataStr = d.data_local || d.timestamp || d.created_at;
                          if (!dataStr) return false;
                          const dt = new Date(dataStr);
                          return dt.getFullYear() === hora.getFullYear() &&
                            dt.getMonth() === hora.getMonth() &&
                            dt.getDate() === hora.getDate() &&
                            dt.getHours() === hora.getHours();
                        });
                        
                        const total = sumSensorValues(dadosHora, 'rain_mm');
                        chuvaPorPeriodo.push(total === null ? null : Number(total.toFixed(2)));
                      }
                    } else {
                      for (let i = 0; i < diffDias; i++) {
                        const dia = new Date(ini.getTime() + i * 24 * 60 * 60 * 1000);
                        const label = `${dia.getDate().toString().padStart(2, '0')}/${(dia.getMonth()+1).toString().padStart(2, '0')}`;
                        categorias.push(label);
                        
                        const dadosDia = dadosPeriodo.filter(d => {
                          const dataStr = d.data_local || d.timestamp || d.created_at;
                          if (!dataStr) return false;
                          const dt = new Date(dataStr);
                          return dt.getFullYear() === dia.getFullYear() &&
                            dt.getMonth() === dia.getMonth() &&
                            dt.getDate() === dia.getDate();
                        });
                        
                        const total = sumSensorValues(dadosDia, 'rain_mm');
                        chuvaPorPeriodo.push(total === null ? null : Number(total.toFixed(2)));
                      }
                    }
                    
                    return (
                      <ApexCharts
                        type="line"
                        height={160}
                        options={{
                          chart: { id: 'rain-mm', toolbar: { show: false }, zoom: { enabled: false } },
                          xaxis: {
                            categories: categorias,
                            tickAmount: isMobile ? 6 : undefined,
                            title: { text: usarAgrupamentoPorHora ? 'Hora' : 'Dia', style: { fontSize: 12 } },
                            labels: {
                              rotate: -45,
                              rotateAlways: false,
                              hideOverlappingLabels: true,
                              maxHeight: 80,
                              style: { fontSize: '10px' },
                            },
                          },
                          yaxis: {
                            labels: { 
                              formatter: val => val !== null ? Number(val).toFixed(1) : '-',
                              style: { fontSize: '11px' }
                            }
                          },
                          stroke: { curve: 'smooth', width: 2.5 },
                          colors: ['#43cea2'],
                          tooltip: { enabled: true },
                          grid: { borderColor: '#e0e7ef' },
                        }}
                        series={[{ name: usarAgrupamentoPorHora ? 'Chuva total' : 'Chuva acumulada', data: chuvaPorPeriodo }]}
                      />
                    );
                  })()}
                </div>

                {/* Umidade relativa agrupada dinamicamente */}
                {sensorSelecionado.dados.some(d => d.rh_perc !== null && d.rh_perc !== undefined) && (
                  <div className="sensor-chart-card is-humidity" style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, color: '#00bcd4', marginBottom: 10, fontSize: 15 }}>
                      Umidade relativa (%) - {usarAgrupamentoPorHora ? 'por hora' : 'por dia'}
                    </div>
                    {(() => {
                      const categorias = [];
                      const umiPorPeriodo = [];
                      
                      if (usarAgrupamentoPorHora) {
                        const totalHoras = Math.min(48, Math.ceil((fim - ini) / (1000 * 60 * 60)));
                        let ultimoDia = null;
                        for (let i = 0; i < totalHoras; i++) {
                          const hora = new Date(ini.getTime() + i * 60 * 60 * 1000);
                          const diaAtual = hora.getDate();
                          const label = (ultimoDia !== diaAtual && totalHoras > 24) 
                            ? `${hora.getDate().toString().padStart(2, '0')}/${(hora.getMonth()+1).toString().padStart(2, '0')} ${hora.getHours().toString().padStart(2, '0')}h`
                            : `${hora.getHours().toString().padStart(2, '0')}h`;
                          ultimoDia = diaAtual;
                          categorias.push(label);
                          
                          const dadosHora = dadosPeriodo.filter(d => {
                            const dataStr = d.data_local || d.timestamp || d.created_at;
                            if (!dataStr) return false;
                            const dt = new Date(dataStr);
                            return dt.getFullYear() === hora.getFullYear() &&
                              dt.getMonth() === hora.getMonth() &&
                              dt.getDate() === hora.getDate() &&
                              dt.getHours() === hora.getHours();
                          });
                          
                          const media = averageSensorValues(dadosHora, 'rh_perc');
                          umiPorPeriodo.push(media === null ? null : Number(media.toFixed(2)));
                        }
                      } else {
                        for (let i = 0; i < diffDias; i++) {
                          const dia = new Date(ini.getTime() + i * 24 * 60 * 60 * 1000);
                          const label = `${dia.getDate().toString().padStart(2, '0')}/${(dia.getMonth()+1).toString().padStart(2, '0')}`;
                          categorias.push(label);
                          
                          const dadosDia = dadosPeriodo.filter(d => {
                            const dataStr = d.data_local || d.timestamp || d.created_at;
                            if (!dataStr) return false;
                            const dt = new Date(dataStr);
                            return dt.getFullYear() === dia.getFullYear() &&
                              dt.getMonth() === dia.getMonth() &&
                              dt.getDate() === dia.getDate();
                          });
                          
                          const media = averageSensorValues(dadosDia, 'rh_perc');
                          umiPorPeriodo.push(media === null ? null : Number(media.toFixed(2)));
                        }
                      }
                      
                      if (umiPorPeriodo.filter(v => v !== null).length === 0) return null;
                      
                      return (
                        <ApexCharts
                          type="line"
                          height={160}
                          options={{
                            chart: { id: 'umidade-relativa', toolbar: { show: false }, zoom: { enabled: false } },
                            xaxis: {
                              categories: categorias,
                              tickAmount: isMobile ? 6 : undefined,
                              title: { text: usarAgrupamentoPorHora ? 'Hora' : 'Dia', style: { fontSize: 12 } },
                              labels: {
                                rotate: -45,
                                rotateAlways: false,
                                hideOverlappingLabels: true,
                                maxHeight: 80,
                                style: { fontSize: '10px' },
                              },
                            },
                            yaxis: {
                              labels: { 
                                formatter: val => val !== null ? Number(val).toFixed(1) : '-',
                                style: { fontSize: '11px' }
                              }
                            },
                            stroke: { curve: 'smooth', width: 2.5 },
                            colors: ['#00bcd4'],
                            tooltip: { enabled: true },
                            grid: { borderColor: '#e0e7ef' },
                          }}
                          series={[{ name: 'Umidade média', data: umiPorPeriodo }]}
                        />
                      );
                    })()}
                  </div>
                )}
              </>
            );
            })() : null}

            {/* Gráfico de Umidade do Solo */}
            {sensorSelecionado.tipo_sensor === 'umidade_solo' && sensorSelecionado.dados && sensorSelecionado.dados.length > 0 && dataIni && dataFim && (() => {
              let ini, fim;
              if (periodoSelecionado === '24h') { fim = new Date(); ini = new Date(fim.getTime() - 24 * 60 * 60 * 1000); }
              else { ini = new Date(dataIni + 'T00:00:00'); fim = new Date(dataFim + 'T23:59:59.999'); }
              const dadosPeriodo = sensorSelecionado.dados
                .filter(d => { const dt = new Date(d.timestamp); return dt >= ini && dt <= fim; })
                .slice()
                .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
              if (!dadosPeriodo.length) {
                return (
                  <div style={{ padding: '40px 20px', textAlign: 'center', background: '#f7fafd', borderRadius: 12, border: '2px dashed #e0e7ef', color: '#888', fontSize: 16, fontWeight: 600 }}>
                    📊 Sem dados disponíveis para o período selecionado
                  </div>
                );
              }
              const categorias = dadosPeriodo.map(d => {
                const dt = new Date(d.timestamp);
                return `${dt.getDate().toString().padStart(2, '0')}/${(dt.getMonth() + 1).toString().padStart(2, '0')} ${dt.getHours().toString().padStart(2, '0')}h`;
              });
              const soloSerie = dadosPeriodo.map(d => parseSensorNumber(d.umidade_solo_perc));
              const tempSoloSerie = dadosPeriodo.map(d => parseSensorNumber(d.temp_solo));
              const temTempSolo = tempSoloSerie.some(v => v != null);
              return (
                <>
                  <div className="sensor-chart-card is-soil" style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, color: '#43a047', marginBottom: 10, fontSize: 15 }}>Umidade do Solo (%)</div>
                    <ApexCharts
                      type="line"
                      height={180}
                      options={{
                        chart: { id: 'solo-umidade', toolbar: { show: false }, zoom: { enabled: false } },
                        xaxis: { categories: categorias, tickAmount: isMobile ? 6 : undefined, labels: { rotate: -45, hideOverlappingLabels: true, style: { fontSize: '10px' } } },
                        yaxis: { labels: { formatter: val => val != null ? Number(val).toFixed(1) : '-' } },
                        stroke: { curve: 'smooth', width: 2.5 },
                        colors: ['#43a047'],
                        tooltip: { enabled: true },
                        grid: { borderColor: '#e0e7ef' },
                      }}
                      series={[{ name: 'Umidade do solo', data: soloSerie }]}
                    />
                  </div>
                  {temTempSolo && (
                    <div className="sensor-chart-card is-temperature" style={{ marginBottom: 20 }}>
                      <div style={{ fontWeight: 700, color: '#1976d2', marginBottom: 10, fontSize: 15 }}>Temperatura do Solo (°C)</div>
                      <ApexCharts
                        type="line"
                        height={160}
                        options={{
                          chart: { id: 'solo-temp', toolbar: { show: false }, zoom: { enabled: false } },
                          xaxis: { categories: categorias, tickAmount: isMobile ? 6 : undefined, labels: { rotate: -45, hideOverlappingLabels: true, style: { fontSize: '10px' } } },
                          yaxis: { labels: { formatter: val => val != null ? Number(val).toFixed(1) : '-' } },
                          stroke: { curve: 'smooth', width: 2.5 },
                          colors: ['#1976d2'],
                          tooltip: { enabled: true },
                          grid: { borderColor: '#e0e7ef' },
                        }}
                        series={[{ name: 'Temperatura do solo', data: tempSoloSerie }]}
                      />
                    </div>
                  )}
                </>
              );
            })()}

            {/* Gráfico e histórico de Chuva/Presença de Água (seco/molhado) */}
            {sensorSelecionado.tipo_sensor === 'chuva_presenca' && sensorSelecionado.dados && sensorSelecionado.dados.length > 0 && dataIni && dataFim && (() => {
              let ini, fim;
              if (periodoSelecionado === '24h') { fim = new Date(); ini = new Date(fim.getTime() - 24 * 60 * 60 * 1000); }
              else { ini = new Date(dataIni + 'T00:00:00'); fim = new Date(dataFim + 'T23:59:59.999'); }
              const dadosPeriodo = sensorSelecionado.dados
                .filter(d => { const dt = new Date(d.timestamp); return dt >= ini && dt <= fim; })
                .slice()
                .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
              if (!dadosPeriodo.length) {
                return (
                  <div style={{ padding: '40px 20px', textAlign: 'center', background: '#f7fafd', borderRadius: 12, border: '2px dashed #e0e7ef', color: '#888', fontSize: 16, fontWeight: 600 }}>
                    📊 Sem dados disponíveis para o período selecionado
                  </div>
                );
              }
              const categorias = dadosPeriodo.map(d => {
                const dt = new Date(d.timestamp);
                return `${dt.getDate().toString().padStart(2, '0')}/${(dt.getMonth() + 1).toString().padStart(2, '0')} ${dt.getHours().toString().padStart(2, '0')}:${dt.getMinutes().toString().padStart(2, '0')}`;
              });
              const estadoSerie = dadosPeriodo.map(d => Number(d.molhado));
              const transicoes = [];
              let ultimoEstado = null;
              for (const d of dadosPeriodo) {
                const est = Number(d.molhado);
                if (est !== ultimoEstado) { transicoes.push({ timestamp: d.timestamp, estado: est }); ultimoEstado = est; }
              }
              const totalMolhado = estadoSerie.filter(v => v === 1).length;
              return (
                <>
                  <div className="sensor-chart-card is-rain" style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, color: '#0288d1', marginBottom: 10, fontSize: 15 }}>Estado do Sensor</div>
                    <ApexCharts
                      type="line"
                      height={160}
                      options={{
                        chart: { id: 'chuva-estado', toolbar: { show: false }, zoom: { enabled: false } },
                        xaxis: { categories: categorias, tickAmount: isMobile ? 6 : undefined, labels: { rotate: -45, hideOverlappingLabels: true, style: { fontSize: '10px' } } },
                        yaxis: { min: 0, max: 1, tickAmount: 1, labels: { formatter: val => val === 1 ? 'Molhado' : 'Seco' } },
                        stroke: { curve: 'stepline', width: 2.5 },
                        colors: ['#0288d1'],
                        tooltip: { enabled: true },
                        grid: { borderColor: '#e0e7ef' },
                      }}
                      series={[{ name: 'Estado (1=molhado)', data: estadoSerie }]}
                    />
                  </div>
                  <div style={{ fontSize: 13, color: '#444', marginBottom: 16 }}>
                    <b>{totalMolhado}</b> de <b>{dadosPeriodo.length}</b> leituras molhado no período · <b>{transicoes.length}</b> mudança(s) de estado
                  </div>
                  {transicoes.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontWeight: 700, color: '#444', marginBottom: 8, fontSize: 14 }}>Histórico de mudanças</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                        {transicoes.slice().reverse().map((t, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: t.estado === 1 ? 'rgba(2,136,209,0.08)' : 'rgba(255,152,0,0.08)', borderRadius: 6, fontSize: 13 }}>
                            <span>{t.estado === 1 ? '🌧️' : '☀️'}</span>
                            <span style={{ fontWeight: 600, color: t.estado === 1 ? '#0288d1' : '#e65100' }}>{t.estado === 1 ? 'Molhado' : 'Seco'}</span>
                            <span style={{ color: '#888' }}>{new Date(t.timestamp).toLocaleString('pt-BR')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            <section className="sensor-alerts-central-card">
              <span className="sensor-alerts-central-icon">🔔</span>
              <div>
                <strong>Alertas deste sensor estão na Central</strong>
                <p>Crie, consulte e remova as regras de todos os sensores em um único lugar, sem perder a identificação do equipamento.</p>
              </div>
              <button type="button" onClick={() => { setModalAberto(false); onNavigate?.('alertas', { source: 'sensor', deviceId: sensorSelecionado.device_id }); }}>
                Gerenciar alertas <FaArrowRight />
              </button>
            </section>
          </div>
        )}
        </div>
      </Modal>
      
      {/* Modal de Confirmação de Exclusão */}
      {confirmarExclusao.aberto && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: darkMode ? '#2a2a2a' : '#fff',
            borderRadius: 16,
            padding: isMobile ? '1.5rem' : '2rem',
            maxWidth: 400,
            width: '100%',
            boxShadow: darkMode 
              ? '0 8px 32px rgba(0,0,0,0.6)'
              : '0 8px 32px rgba(0,0,0,0.2)',
            border: darkMode ? '1px solid #444' : 'none'
          }}>
            <h3 style={{
              margin: '0 0 1rem 0',
              color: darkMode ? '#f44336' : '#d32f2f',
              fontSize: isMobile ? 18 : 20,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <span style={{ fontSize: 24 }}>⚠️</span>
              Confirmar Exclusão
            </h3>
            <p style={{
              margin: '0 0 1.5rem 0',
              color: darkMode ? '#f0f0f0' : '#444',
              fontSize: 15,
              lineHeight: 1.5
            }}>
              Tem certeza que deseja excluir o sensor <strong>"{confirmarExclusao.nome}"</strong>?
              <br />
              <span style={{ fontSize: 13, color: darkMode ? '#999' : '#666', marginTop: 8, display: 'block' }}>
                Esta ação não pode ser desfeita.
              </span>
            </p>
            <div style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'flex-end',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => setConfirmarExclusao({ aberto: false, id: null, nome: '' })}
                style={{
                  background: darkMode ? '#3a3a3a' : '#eee',
                  color: darkMode ? '#90caf9' : '#1976d2',
                  border: 'none',
                  borderRadius: 8,
                  padding: '0.7rem 1.5rem',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: 'pointer',
                  flex: isMobile ? '1 1 100%' : '0 0 auto',
                  transition: 'all 0.2s'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={() => excluirSensor(confirmarExclusao.id)}
                disabled={loading}
                style={{
                  background: '#d32f2f',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '0.7rem 1.5rem',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  flex: isMobile ? '1 1 100%' : '0 0 auto',
                  opacity: loading ? 0.6 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {loading ? 'Excluindo...' : 'Sim, Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {sensorLocalizacaoMapa && (
        <MapSelectModal
          darkMode={darkMode}
          title={sensorLocalizacaoMapa.mode === 'new'
            ? 'Marcar local de instalação'
            : `Localizar ${sensorLocalizacaoMapa.sensor?.nome_sensor || sensorLocalizacaoMapa.sensor?.device_id || 'sensor'}`}
          description={salvandoLocalizacao
            ? 'Salvando a nova localização…'
            : 'Busque uma cidade, digite latitude e longitude ou toque no mapa para indicar exatamente onde o equipamento está instalado.'}
          initialCenter={sensorLocalizacaoMapa.mode === 'new'
            ? (latitudeCadastro && longitudeCadastro ? { lat: latitudeCadastro, lon: longitudeCadastro } : null)
            : ((sensorLocalizacaoMapa.sensor?.latitude ?? sensorLocalizacaoMapa.sensor?.lat) != null
              && (sensorLocalizacaoMapa.sensor?.longitude ?? sensorLocalizacaoMapa.sensor?.lon) != null
                ? {
                    lat: sensorLocalizacaoMapa.sensor.latitude ?? sensorLocalizacaoMapa.sensor.lat,
                    lon: sensorLocalizacaoMapa.sensor.longitude ?? sensorLocalizacaoMapa.sensor.lon,
                  }
                : null)}
          initialZoom={15}
          onClose={() => !salvandoLocalizacao && setSensorLocalizacaoMapa(null)}
          onSelect={selecionarLocalizacaoNoMapa}
        />
      )}
      
      {/* Modal de Upgrade */}
      <UpgradeModal 
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        recurso={upgradeRecurso}
        planoAtual={plano?.tipo || 'starter'}
        limiteInfo={{}}
        usuario={user}
      />
      </div>
    </div>
  );
}

export default Sensores;
